import * as THREE from 'three';
import {
    computeWorldBoundsExcludingAnimatedDoorParts,
    getDirection,
    getGroupWidth,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    getFloorClearanceMm,
    getFloorClearanceSceneUnits,
} from 'components/customer/RoomPlanner/helpers/placeholder/floorClearance';
import {getCabinetLikeWorldOrigin} from 'components/customer/RoomPlanner/helpers/placeholder/getCabinetLikeWorldOrigin';
import {rotateAroundCenter} from './rotateAroundCenter';
import {
    WALL_FLUSH_TOLERANCE_MM,
    calculateCabinetWallDistances,
    getTrueWallGapForArrow,
} from 'components/customer/RoomPlanner/helpers/measurement/wallDistance';
import {
    getHasDecimalInMeasurementLabelsByOrientation,
    getLShapeLegWidthBySpan,
} from 'components/customer/RoomPlanner/helpers/measurement/measurementData';
import {
    MeasurementConfig,
    RoomConfig,
} from 'components/customer/RoomPlanner/helpers/measurement/types';
import {
    CabinetMesh,
    RoomPlannerProduct,
    RoomPlannerProductWithTemplate,
    UserData,
} from 'components/customer/RoomPlanner/types';
import {calculateDisplayHeight} from 'components/customer/RoomPlanner/helpers/cabinetDimension';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {DISTANCE_LABEL_STYLE} from 'components/customer/RoomPlanner/helpers/measurement/distanceLabelStyle';
import TWEEN, {type Tween} from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';

interface GroupWithUpdatePosition extends THREE.Group {
    userData: {
        updatePosition: (width: number, depth: number, height: number) => void;
    };
}

/**
 * Sync an overlay group (movement arrows, wall-distance guides, dimension
 * labels) to a cabinet. Cabinets are laid flat (rotation.x = -90°) with a
 * corner origin; placeholders stand upright and yaw on Y with a GLB pivot —
 * map them into the same local space so arrow/measurement offsets land on
 * the mesh.
 *
 * @param {THREE.Group} overlay - Overlay group to position
 * @param {THREE.Group} cabinetGroup - Cabinet or placeholder to follow
 */
export const syncOverlayTransformToCabinet = (
    overlay: THREE.Group,
    cabinetGroup: THREE.Group
) => {
    if (!cabinetGroup.userData?.isPlaceholder) {
        overlay.position.copy(cabinetGroup.position);
        overlay.rotation.copy(cabinetGroup.rotation);
        return;
    }

    overlay.rotation.set(-Math.PI / 2, 0, cabinetGroup.rotation.y);
    overlay.position.copy(getCabinetLikeWorldOrigin(cabinetGroup));
};

interface DistanceClickData {
    direction: string;
    screenX: number;
    screenY: number;
    measurement: string;
    measurementObject: THREE.Object3D;
    // Set for the wall-distance pills, which are edited in place.
    bubble?: DistanceBubbleRect;
}

/**
 * Where a wall-distance pill sits on screen (viewport pixels), so an input
 * can be laid exactly over it.
 */
export interface DistanceBubbleRect {
    sprite: THREE.Sprite;
    centerX: number;
    centerY: number;
    height: number;
}

interface CabinetUserData extends THREE.Object3DEventMap {
    onDistanceClick?: (data: DistanceClickData) => void;
    camera?: THREE.Camera;
}

/**
 * Helper function to check if arrow group should be hidden based on direction
 * @param {string} arrowGroupId - The ID of the arrow group
 * @param {string} currentDirection - The current direction of the cabinet
 * @return {boolean} true if the arrow group should be hidden, false otherwise
 */
export const shouldHideArrowGroup = (
    arrowGroupId: string,
    currentDirection: string
): boolean => {
    switch (currentDirection) {
        case 'front':
            return arrowGroupId === 'arrowGroupBack';
        case 'left':
            return arrowGroupId === 'arrowGroupLeft';
        case 'back':
            return arrowGroupId === 'arrowGroupFront';
        case 'right':
            return arrowGroupId === 'arrowGroupRight';
        case 'meet':
            // When direction is "meet", only show arrows with hyphen pattern (e.g., arrowGroupLeft-distance)
            // Hide regular arrow groups without hyphen
            return !arrowGroupId.includes('-');
        default:
            return false;
    }
};

/**
 * Sets high render priority on an ArrowHelper to ensure it's always visible on top of other objects
 * @param {THREE.ArrowHelper} arrow - The ArrowHelper object to configure
 * @param {number} renderOrder - Optional custom render order (defaults to 1000)
 */
export const setArrowHighPriority = (
    arrow: THREE.ArrowHelper,
    renderOrder = 1000
): void => {
    // Set very high render priority to ensure arrows are always visible on top
    arrow.renderOrder = renderOrder;

    // Force the arrow to always be visible by setting it to render in front
    arrow.frustumCulled = false;

    // Disable depth testing for the arrow line to render on top of other objects
    if (
        arrow.line &&
        arrow.line.material &&
        !Array.isArray(arrow.line.material)
    ) {
        arrow.line.material.depthTest = false;
        arrow.line.material.depthWrite = false;
        arrow.line.material.transparent = true;
        arrow.line.material.opacity = 1.0;
    }

    // Disable depth testing for the arrow cone to render on top of other objects
    if (
        arrow.cone &&
        arrow.cone.material &&
        !Array.isArray(arrow.cone.material)
    ) {
        arrow.cone.material.depthTest = false;
        arrow.cone.material.depthWrite = false;
        arrow.cone.material.transparent = true;
        arrow.cone.material.opacity = 1.0;
    }
};

/**
 * Helper function to set visibility for arrays of objects
 * @param {THREE.Object3D[]} objects - Array of THREE.Object3D objects to set visibility for
 * @param {boolean} visible - Whether objects should be visible
 * @return {void}
 */
const setVisibility = (objects: THREE.Object3D[], visible: boolean): void => {
    for (const obj of objects) {
        obj.visible = visible;
        // Also set visibility on all children to ensure they're visible
        obj.traverse((child) => {
            child.visible = visible;
        });
    }
};

/**
 * Measures a wall-distance pill on screen, relative to the planner canvas.
 * The sprites use sizeAttenuation: false under an orthographic camera, so
 * their world height maps to pixels through the camera's zoom and frustum.
 * @param {THREE.Sprite} sprite - The pill sprite
 * @param {THREE.Vector3} projected - The sprite's position in NDC
 * @param {THREE.Camera} camera - The camera rendering the planner
 * @return {DistanceBubbleRect | undefined} The pill's rect, if measurable
 */
const getDistanceBubbleRect = (
    sprite: THREE.Sprite,
    projected: THREE.Vector3,
    camera: THREE.Camera
): DistanceBubbleRect | undefined => {
    const container = document.getElementById('3d-room-planner');
    const ortho = camera as THREE.OrthographicCamera;
    if (!container || !ortho.isOrthographicCamera) return undefined;

    const rect = container.getBoundingClientRect();
    const pixelsPerUnit =
        (rect.height * ortho.zoom) / (ortho.top - ortho.bottom || 1);

    // Measure the resting size, not the enlarged hover size.
    const restingScale =
        (sprite.userData.originalScale as THREE.Vector3 | undefined) ??
        sprite.scale;
    const worldScale = sprite.getWorldScale(new THREE.Vector3());
    const worldHeight = worldScale.y * (restingScale.y / sprite.scale.y);

    return {
        sprite,
        centerX: rect.left + (projected.x * 0.5 + 0.5) * rect.width,
        centerY: rect.top + (-projected.y * 0.5 + 0.5) * rect.height,
        height: worldHeight * pixelsPerUnit,
    };
};

/**
 * Helper function to create a measurement text sprite
 * @param {number} measurement - The measurement value in mm
 * @param {string} direction - The direction of the measurement
 * @param {THREE.Group} arrowGroup - The arrow group this sprite belongs to
 * @param {THREE.Group} cabinetGroup - The cabinet group
 * @param {Object} config - Label configuration object
 * @return {THREE.Sprite | null} The created text sprite or null if measurement is invalid
 */
const createMeasurementTextSprite = (
    measurement: number,
    direction: string,
    arrowGroup: THREE.Group,
    cabinetGroup: THREE.Group,
    config: {
        canvas: {height: number};
        pill: {
            paddingX: number;
            radius: number;
            borderWidth: number;
            fill: string;
            border: string;
        };
        font: {
            style: string;
            color: string;
            align: CanvasTextAlign;
            baseline: CanvasTextBaseline;
        };
        sprite: {
            scale: {x: number; y: number; z: number};
            material: {
                transparent: boolean;
                depthTest: boolean;
                depthWrite: boolean;
                alphaTest: number;
                sizeAttenuation: boolean;
            };
        };
    }
): THREE.Sprite | null => {
    if (
        typeof measurement !== 'number' ||
        !Number.isFinite(measurement) ||
        measurement <= 1
    ) {
        return null;
    }
    const labelText = measurement;
    const {pill} = config;

    const canvas = document.createElement('canvas');
    canvas.style.backgroundColor = 'transparent';

    const context = canvas.getContext('2d');
    if (!context) {
        return null;
    }

    // Size the pill to the number; resizing the canvas resets the context,
    // so the font is set again before drawing.
    context.font = config.font.style;
    const textWidth = context.measureText(String(labelText)).width;
    canvas.height = config.canvas.height;
    canvas.width = Math.ceil(textWidth + pill.paddingX * 2);

    // Rounded badge, inset by half the border so the stroke isn't clipped.
    const inset = pill.borderWidth / 2;
    const right = canvas.width - inset;
    const bottom = canvas.height - inset;
    const radius = Math.min(
        pill.radius,
        (canvas.height - pill.borderWidth) / 2
    );

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.beginPath();
    context.moveTo(inset + radius, inset);
    context.arcTo(right, inset, right, bottom, radius);
    context.arcTo(right, bottom, inset, bottom, radius);
    context.arcTo(inset, bottom, inset, inset, radius);
    context.arcTo(inset, inset, right, inset, radius);
    context.closePath();
    context.fillStyle = pill.fill;
    context.fill();
    context.lineWidth = pill.borderWidth;
    context.strokeStyle = pill.border;
    context.stroke();

    context.font = config.font.style;
    context.textAlign = config.font.align;
    context.textBaseline = config.font.baseline;
    context.fillStyle = config.font.color;
    context.fillText(String(labelText), canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    texture.premultiplyAlpha = true;

    const textMaterial = new THREE.SpriteMaterial({
        map: texture,
        transparent: config.sprite.material.transparent,
        depthTest: config.sprite.material.depthTest,
        depthWrite: config.sprite.material.depthWrite,
        alphaTest: config.sprite.material.alphaTest,
        sizeAttenuation: config.sprite.material.sizeAttenuation,
    });

    const textSprite = new THREE.Sprite(textMaterial);
    textSprite.userData.clickable = true;
    textSprite.userData.isTextSprite = true;
    textSprite.userData.measurementType = direction;

    // Add hover effect
    // Hover zoom, eased in and out. Kept in this closure rather than
    // userData, which three.js copies as JSON when objects are cloned.
    let scaleTween: Tween<THREE.Vector3> | null = null;
    const zoomTo = (factor: number) => {
        if (!textSprite.userData.originalScale) {
            textSprite.userData.originalScale = textSprite.scale.clone();
        }
        const origScale = textSprite.userData.originalScale as THREE.Vector3;

        scaleTween?.stop();
        scaleTween = new TWEEN.Tween(textSprite.scale, USE_LEGACY_TWEEN_GROUP)
            .to(
                {x: origScale.x * factor, y: origScale.y * factor},
                DISTANCE_LABEL_STYLE.hoverDurationMs
            )
            .easing(TWEEN.Easing.Cubic.Out)
            .start();
    };

    textSprite.userData.onHover = () => {
        if (!arrowGroup.visible) {
            return;
        }
        zoomTo(DISTANCE_LABEL_STYLE.hoverScale);
    };

    textSprite.userData.onHoverEnd = () => {
        // While its number is edited the pill stays zoomed in; the editor
        // calls this again once it closes.
        if (!arrowGroup.visible || textSprite.userData.isEditing) {
            return;
        }
        if (textSprite.userData.originalScale) {
            zoomTo(1);
        }
    };

    textSprite.userData.onClick = (
        event: MouseEvent,
        raycaster: THREE.Raycaster,
        camera: THREE.Camera
    ) => {
        cabinetGroup.userData.camera = camera;
        if (!arrowGroup.visible) {
            return;
        }

        const worldPosition = textSprite.getWorldPosition(new THREE.Vector3());
        const vector = worldPosition.clone();
        vector.project(camera);

        const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
        const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;

        if ((cabinetGroup.userData as CabinetUserData).onDistanceClick) {
            (cabinetGroup.userData as CabinetUserData).onDistanceClick({
                direction,
                screenX: x,
                screenY: y,
                measurement: String(labelText),
                measurementObject: textSprite.parent,
                bubble: getDistanceBubbleRect(textSprite, vector, camera),
            });
        }
    };

    // Keep the canvas aspect ratio so the pill isn't stretched.
    textSprite.scale.set(
        config.sprite.scale.y * (canvas.width / canvas.height),
        config.sprite.scale.y,
        config.sprite.scale.z
    );

    return textSprite;
};

/**
 * Helper function to get text sprite position based on direction and cabinet orientation
 * @param {string} direction - The measurement direction (left, right, front, back)
 * @param {string} currentDirection - The current cabinet orientation (front, left, right, back)
 * @param {number} actualArrowLength - The actual arrow length
 * @param {number} width - Cabinet width
 * @param {number} depth - Cabinet depth
 * @return {THREE.Vector3} The position vector for the text sprite
 */
const getTextSpritePosition = (
    direction: string,
    currentDirection: string,
    actualArrowLength: number,
    width: number,
    depth: number
): THREE.Vector3 => {
    // Use explicit conditionals to avoid dynamic property access
    if (currentDirection === 'front') {
        if (direction === 'left') {
            return new THREE.Vector3(
                -Math.abs(actualArrowLength) / 2,
                depth / 2,
                0
            );
        }
        if (direction === 'right') {
            return new THREE.Vector3(
                Math.abs(actualArrowLength) / 2 + width,
                depth / 2,
                0
            );
        }
        if (direction === 'front') {
            return new THREE.Vector3(width / 2, -actualArrowLength / 2, 0);
        }
        if (direction === 'back') {
            return new THREE.Vector3(
                width / 2,
                depth + actualArrowLength / 2,
                0
            );
        }
    }
    if (currentDirection === 'left') {
        if (direction === 'left') {
            return new THREE.Vector3(
                -Math.abs(actualArrowLength) / 2,
                width / 2,
                0
            );
        }
        if (direction === 'right') {
            return new THREE.Vector3(
                depth + actualArrowLength / 2,
                width / 2,
                0
            );
        }
        if (direction === 'front') {
            return new THREE.Vector3(depth / 2, -actualArrowLength / 2, 0);
        }
        if (direction === 'back') {
            return new THREE.Vector3(
                depth / 2,
                width + actualArrowLength / 2,
                0
            );
        }
    }
    if (currentDirection === 'right') {
        if (direction === 'left') {
            return new THREE.Vector3(
                -Math.abs(actualArrowLength) / 2,
                width / 2,
                0
            );
        }
        if (direction === 'right') {
            return new THREE.Vector3(
                depth + actualArrowLength / 2,
                width / 2,
                0
            );
        }
        if (direction === 'front') {
            return new THREE.Vector3(depth / 2, -actualArrowLength / 2, 0);
        }
        if (direction === 'back') {
            return new THREE.Vector3(
                depth / 2,
                width + actualArrowLength / 2,
                0
            );
        }
    }
    if (currentDirection === 'back') {
        if (direction === 'left') {
            return new THREE.Vector3(
                width + actualArrowLength / 2,
                depth / 2,
                0
            );
        }
        if (direction === 'right') {
            return new THREE.Vector3(
                -Math.abs(actualArrowLength) / 2,
                depth / 2,
                0
            );
        }
        if (direction === 'front') {
            return new THREE.Vector3(
                width / 2,
                depth + actualArrowLength / 2,
                0
            );
        }
        if (direction === 'back') {
            return new THREE.Vector3(width / 2, -actualArrowLength / 2, 0);
        }
    }
    return new THREE.Vector3(0, 0, 0);
};

export const addArrowsAndText = (
    wall: THREE.Mesh,
    width: number,
    handleArrows: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial)[]
    ) => void,
    isSideWall: boolean, // Changed to boolean with default value
    measurement = 0,
    wallHeight = 0,
    id = ''
) => {
    // Arrow Helper
    const arrowDir = new THREE.Vector3(1, 0, 0).normalize();
    const arrowLength = width - 50 - (isSideWall ? 25 : 0);
    const arrowColor = '#333';
    const wallHeightOffset = 30;
    const spriteOffset = 50;

    // Create left arrow
    const leftArrow = new THREE.ArrowHelper(
        arrowDir.clone().negate(), // Direction
        new THREE.Vector3(
            width - 75 + (isSideWall ? 25 : 0),
            wallHeight + wallHeightOffset,
            12.5
        ), // Start point
        arrowLength,
        arrowColor,
        8,
        8
    );
    wall.add(leftArrow);

    // Create right arrow
    const rightArrow = new THREE.ArrowHelper(
        arrowDir,
        new THREE.Vector3(width + 75, wallHeight + wallHeightOffset, 12.5),
        arrowLength,
        arrowColor,
        8,
        8
    );
    wall.add(rightArrow);

    // Text for width
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 256;
    canvas.height = 128;

    context.font = 'bold 80px Arial';
    context.fillStyle = '#000';
    context.textAlign = 'center';
    context.fillText(`${measurement}`, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const textMaterial = new THREE.SpriteMaterial({map: texture});
    const textSprite = new THREE.Sprite(textMaterial);
    textSprite.userData.text = `${measurement}`;
    textSprite.position.set(
        width + (isSideWall ? 25 : 0),
        wallHeight + wallHeightOffset + spriteOffset,
        20
    );
    textSprite.scale.set(100, 50, 1);

    wall.add(textSprite);

    rightArrow.userData.id = id;
    leftArrow.userData.id = id;
    textMaterial.userData.id = id;

    handleArrows([rightArrow, leftArrow, textMaterial]);

    leftArrow.visible = false;
    rightArrow.visible = false;
    textMaterial.visible = false;
};

export const addArrowsAndTextForCabinetDimension = (
    wall: THREE.Mesh,
    width: number,
    handleArrowsAndGroup: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial)[],
        arrowGroup?: THREE.Group<THREE.Object3DEventMap>
    ) => void,
    isSideWall = false,
    isLshapedRoom = false,
    isTopWall = false,
    id = '',
    scale?: number
) => {
    const arrowDir = new THREE.Vector3(1, 0, 0).normalize();
    const reverseArrowDir = arrowDir.clone().negate();
    const arrowLength = width - 10;
    const arrowColor = id === 'arrowGroupLeft-distance' ? '#A9A9A9' : 0x333333;
    const arrowHeadLength = 0;
    const arrowHeadWidth = 0;

    const createArrow = (
        origin: THREE.Vector3,
        direction: THREE.Vector3,
        arrowLength: number
    ) => {
        const arrow = new THREE.ArrowHelper(
            direction,
            origin,
            arrowLength,
            arrowColor,
            arrowHeadLength,
            arrowHeadWidth
        );

        // Set high render priority to ensure arrows are always visible on top
        setArrowHighPriority(arrow);

        return arrow;
    };

    // Create left and right arrows
    const leftArrowOrigin = new THREE.Vector3(width, 25, 40);
    const rightArrowOrigin = new THREE.Vector3(15, 25, 40);

    const leftArrow = createArrow(
        leftArrowOrigin,
        reverseArrowDir,
        arrowLength
    );
    const rightArrow = createArrow(rightArrowOrigin, arrowDir, arrowLength);

    const arrowGroup = new THREE.Group();
    arrowGroup.userData.id =
        id ||
        (isSideWall && isTopWall
            ? 'arrowGroupLeftTopWall'
            : isSideWall
              ? 'arrowGroupLeft'
              : isTopWall
                ? 'arrowGroupTopTopWall'
                : 'arrowGroupTop');

    // Set very high render order to ensure arrows are always on top
    arrowGroup.renderOrder = 1001;

    arrowGroup.add(rightArrow);
    arrowGroup.add(leftArrow);

    wall.add(arrowGroup);

    const createTextMesh = (
        text: string,
        position: THREE.Vector3,
        textColor = 'black'
    ) => {
        const canvas = document.createElement('canvas');
        canvas.width = 384; // Increased from 256
        canvas.height = 192; // Increased from 128
        canvas.style.backgroundColor = 'transparent';

        const context = canvas.getContext('2d');
        if (!context) return null;

        // Clear the canvas to make it transparent
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Text - larger and bolder for better visibility
        context.font = 'bold 100px Arial'; // Increased from 40px
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = textColor;

        // Add padding around text to prevent cropping
        context.fillText(text, canvas.width / 2, canvas.height / 2);

        // Create texture
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        texture.premultiplyAlpha = true;

        // Create sprite material
        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            alphaTest: 0.01,
            sizeAttenuation: false,
        });

        // Create sprite - larger scale
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.scale.set(60, 30, 1); // Increased from 40, 20, 1

        // Ensure sprite always renders on top
        sprite.renderOrder = 999;
        sprite.position.copy(position);

        return sprite;
    };

    let textMesh = createTextMesh(``, new THREE.Vector3(width, 0, 41));

    // Helper function to calculate cabinet label based on arrow group and direction
    const calculateCabinetLabel = ({
        arrowGroupId,
        currentDirection,
        cabinetValues,
        isPanelProduct,
        isPanelProductWithLength,
        height,
        isManualPanel,
        isPanelProductWithWidth,
        isUShapedPanel,
        userData,
    }: {
        arrowGroupId: string;
        currentDirection: string;
        cabinetValues: RoomPlannerProductWithTemplate;
        isPanelProduct: boolean;
        isPanelProductWithLength: boolean;
        height?: number;
        isManualPanel?: boolean;
        isPanelProductWithWidth?: boolean;
        isUShapedPanel?: boolean;
        userData: UserData;
    }): string | number | undefined => {
        if (arrowGroupId === 'arrowGroupLeft-distance') {
            return height;
        }

        const isFrontBackDirection =
            currentDirection === 'front' || currentDirection === 'back';

        // Special handling for manual panels - only affects text label, not arrow dimensions
        // Must consider rotation: when panel is rotated, width/length swap relative to walls
        if (isManualPanel) {
            // Manual panels: Width = cabinet_panel_width, Length = cabinet_panel_length
            // When rotated, the dimension visible on each wall changes:
            // - Back/Front walls measure horizontally (X-axis)
            // - Left/Right walls measure vertically (Z-axis)
            // - Panel facing 'front'/'back': width=X, length=Z
            // - Panel facing 'left'/'right': width=Z, length=X (swapped)

            const isHorizontalWall =
                arrowGroupId === 'arrowGroupFront' ||
                arrowGroupId === 'arrowGroupBack';
            const isVerticalWall =
                arrowGroupId === 'arrowGroupRight' ||
                arrowGroupId === 'arrowGroupLeft';
            const isRotated =
                currentDirection === 'left' || currentDirection === 'right';

            if (isHorizontalWall) {
                // Horizontal walls: show width if panel faces front/back, length if rotated left/right
                return isRotated
                    ? cabinetValues.cabinet_panel_length
                    : cabinetValues.cabinet_panel_width;
            } else if (isVerticalWall) {
                // Vertical walls: show length if panel faces front/back, width if rotated left/right
                return isRotated
                    ? cabinetValues.cabinet_panel_width
                    : cabinetValues.cabinet_panel_length;
            }
        }

        // Get base dimension based on direction
        let baseDimension = isFrontBackDirection
            ? cabinetValues.cabinet_width
            : cabinetValues.cabinet_depth;

        if (arrowGroupId === 'arrowGroupRight') {
            baseDimension = isFrontBackDirection
                ? cabinetValues.cabinet_depth
                : cabinetValues.cabinet_width;
        }
        // Override for panel products
        if (isPanelProduct) {
            if (
                arrowGroupId === 'arrowGroupFront' ||
                arrowGroupId === 'arrowGroupBack'
            ) {
                baseDimension = isFrontBackDirection
                    ? cabinetValues.cabinet_ext_thickness
                    : cabinetValues.applied_panel_depth;
            } else if (
                arrowGroupId === 'arrowGroupRight' ||
                arrowGroupId === 'arrowGroupLeft'
            ) {
                baseDimension = isFrontBackDirection
                    ? cabinetValues.cabinet_applied_panel_depth
                    : cabinetValues.cabinet_ext_thickness;
            }
        } else if (isPanelProductWithLength) {
            if (
                arrowGroupId === 'arrowGroupFront' ||
                arrowGroupId === 'arrowGroupBack'
            ) {
                baseDimension = isFrontBackDirection
                    ? cabinetValues.cabinet_carc_thickness
                    : cabinetValues.cabinet_panel_length;
            } else if (
                arrowGroupId === 'arrowGroupRight' ||
                arrowGroupId === 'arrowGroupLeft'
            ) {
                baseDimension = isFrontBackDirection
                    ? cabinetValues.cabinet_panel_length
                    : cabinetValues.cabinet_carc_thickness;
            }
        } else if (isPanelProductWithWidth) {
            if (
                arrowGroupId === 'arrowGroupFront' ||
                arrowGroupId === 'arrowGroupBack'
            ) {
                baseDimension = isFrontBackDirection
                    ? cabinetValues.cabinet_carc_thickness
                    : cabinetValues.cabinet_panel_width;
            } else if (
                arrowGroupId === 'arrowGroupRight' ||
                arrowGroupId === 'arrowGroupLeft'
            ) {
                baseDimension = isFrontBackDirection
                    ? cabinetValues.cabinet_panel_width
                    : cabinetValues.cabinet_carc_thickness;
            }
        } else if (isUShapedPanel) {
            if (
                arrowGroupId === 'arrowGroupFront' ||
                arrowGroupId === 'arrowGroupBack'
            ) {
                baseDimension = isFrontBackDirection
                    ? cabinetValues.cabinet_panel_length
                    : cabinetValues.cabinet_panel_width;
            } else if (
                arrowGroupId === 'arrowGroupRight' ||
                arrowGroupId === 'arrowGroupLeft'
            ) {
                baseDimension = isFrontBackDirection
                    ? cabinetValues.cabinet_panel_width
                    : cabinetValues.cabinet_panel_length;
            }
        } else if (userData.isPlaceholder) {
            // Same fields as cabinets (synced on placeholder load/edit).
            const width = Number(
                cabinetValues.cabinet_width ??
                    (
                        cabinetValues as unknown as {
                            actualDimensions?: {width?: number};
                        }
                    )?.actualDimensions?.width
            );
            const depth = Number(
                cabinetValues.cabinet_depth ??
                    (
                        cabinetValues as unknown as {
                            actualDimensions?: {depth?: number};
                        }
                    )?.actualDimensions?.depth
            );
            if (Number.isFinite(width) && Number.isFinite(depth)) {
                if (
                    arrowGroupId === 'arrowGroupFront' ||
                    arrowGroupId === 'arrowGroupBack'
                ) {
                    baseDimension = isFrontBackDirection ? width : depth;
                } else {
                    baseDimension = isFrontBackDirection ? depth : width;
                }
            }
        }
        return baseDimension;
    };

    // Helper function to calculate text position
    const calculateTextPosition = (
        arrowGroupId: string,
        currentDirection: string,
        newWidth: number,
        textWidth: number,
        isSideWall: boolean,
        isLshapedRoom: boolean,
        cabinetHeight: number | string
    ): {x: number; y: number} => {
        const TEXT_OFFSET_Y = 0;
        const SIDE_WALL_OFFSET = 15;
        const SMALL_HEIGHT_THRESHOLD = 100;

        let textMeshPositionX = newWidth / 2 - textWidth / 2;
        let textMeshPositionY = TEXT_OFFSET_Y;

        // Special positioning for different room types
        if (isLshapedRoom) {
            textMeshPositionX = newWidth - SIDE_WALL_OFFSET - textWidth / 2;
        } else if (isSideWall) {
            textMeshPositionX = newWidth / 2 - textWidth / 2;
        } else {
            // Calculate true midpoint between arrows

            const leftArrowPos = newWidth;
            const rightArrowPos = 25;
            const arrowSpaceWidth = leftArrowPos - rightArrowPos;
            const trueMidpoint = rightArrowPos + arrowSpaceWidth / 2;
            textMeshPositionX = trueMidpoint;
        }

        // Special positioning for left arrow groups with small heights
        if (
            (arrowGroupId === 'arrowGroupLeft-distance' ||
                arrowGroupId === 'arrowGroupLeft') &&
            typeof cabinetHeight === 'number' &&
            cabinetHeight < SMALL_HEIGHT_THRESHOLD
        ) {
            if (arrowGroupId === 'arrowGroupLeft-distance') {
                if (currentDirection === 'left') {
                    textMeshPositionY = 10;
                }
                textMeshPositionX += 10;
            } else {
                if (currentDirection === 'left') {
                    textMeshPositionX += 100;
                } else {
                    textMeshPositionY = 30;
                    textMeshPositionX += isLshapedRoom ? 55 : 30;
                }
            }
        } else if (arrowGroupId === 'arrowGroupLeft' && isLshapedRoom) {
            textMeshPositionX = newWidth / 2;
        }

        return {x: textMeshPositionX, y: textMeshPositionY};
    };

    // Helper function to apply text positioning based on arrow group type
    const applyTextPositioning = (
        textMesh: THREE.Sprite,
        arrowGroupId: string,
        currentDirection: string,
        position: {x: number; y: number},
        isSideWall: boolean,
        cabinetHeight: number | string,
        isLshapedRoom: boolean
    ): void => {
        const Z_POSITION_SIDE_WALL = 41;
        const Z_POSITION_NORMAL = 41;
        const Z_POSITION_FRONT = 40;
        const ROTATION_OFFSET = 15;

        // Set base position
        textMesh.position.set(
            position.x + 30,
            position.y,
            isSideWall ? Z_POSITION_SIDE_WALL : Z_POSITION_NORMAL
        );

        // Special positioning for back arrow group
        if (arrowGroupId === 'arrowGroupBack') {
            textMesh.position.set(position.x, 20, Z_POSITION_NORMAL);
        }

        // Special positioning for front arrow group
        if (arrowGroupId === 'arrowGroupFront') {
            if (currentDirection === 'front') {
                textMesh.position.set(position.x, 20, Z_POSITION_FRONT);
            } else if (currentDirection === 'left') {
                if (typeof cabinetHeight === 'number' && cabinetHeight < 100) {
                    textMesh.rotateY(Math.PI);
                    textMesh.position.set(
                        position.x - ROTATION_OFFSET,
                        10,
                        Z_POSITION_NORMAL
                    );
                } else {
                    if (!isLshapedRoom) {
                        textMesh.position.set(
                            position.x - ROTATION_OFFSET,
                            10,
                            Z_POSITION_NORMAL
                        );
                    }
                }
            } else if (currentDirection === 'right') {
                if (typeof cabinetHeight === 'number' && cabinetHeight < 100) {
                    textMesh.rotateY(Math.PI);
                    textMesh.position.set(
                        position.x - ROTATION_OFFSET,
                        10,
                        Z_POSITION_NORMAL
                    );
                } else {
                    textMesh.position.set(
                        position.x - ROTATION_OFFSET,
                        10,
                        Z_POSITION_NORMAL
                    );
                }
            }
        }
    };

    // Helper function to apply text rotations
    const applyTextRotations = (
        textMesh: THREE.Sprite,
        isSideWall: boolean,
        isLshapedRoom: boolean,
        isLShapePanel: boolean
    ): void => {
        textMesh.rotateX(-Math.PI / 2);

        if (!isSideWall && !isLshapedRoom && !isLShapePanel) {
            textMesh.rotateZ(Math.PI);
        }
    };

    arrowGroup.userData.onDrag = (
        width: number,
        depth: number,
        cabinetGroup?: THREE.Group<THREE.Object3DEventMap>,
        height?: number
    ) => {
        // Early return if no cabinet group
        if (!cabinetGroup) return;

        // Set the entire group visible
        arrowGroup.visible = true;
        arrowGroup.traverse((child) => {
            child.visible = true;
        });

        const userData = cabinetGroup.userData as UserData;

        const currentDirection = userData.currentDirection;
        const arrowGroupId = arrowGroup.userData.id as string;

        // Hide arrow group if it should be hidden based on direction
        if (shouldHideArrowGroup(arrowGroupId, currentDirection)) {
            arrowGroup.visible = false;
            return;
        }

        // Calculate dimensions
        const isPanelProduct = userData.isPanelProduct;
        const isPanelProductWithLength = userData.isPanelProductWithLength;
        const isPanelProductWithWidth = userData.isPanelProductWithWidth;
        const computedWidth = isSideWall
            ? isPanelProduct
                ? depth
                : depth - 8
            : isPanelProduct
              ? width
              : width - 8;

        const cabinetValues = cabinetGroup.userData
            .values as RoomPlannerProductWithTemplate;

        // Check if this is a manual panel (for text label only)
        const isManualPanel = userData.isManualPanel;
        const isUShapedPanel = userData.isUShapedPanel;
        const isRobeProduct = userData.isRobeProduct;

        let cabinetLabel = calculateCabinetLabel({
            arrowGroupId,
            currentDirection,
            cabinetValues,
            isPanelProduct,
            isPanelProductWithLength,
            height,
            isManualPanel,
            isPanelProductWithWidth,
            isUShapedPanel,
            userData,
        });

        if (
            userData.isLShapedProduct &&
            !isManualPanel &&
            !isPanelProduct &&
            !isPanelProductWithLength &&
            !isPanelProductWithWidth &&
            !isUShapedPanel
        ) {
            const spanMm =
                (isSideWall ? Number(depth) : Number(width)) / (scale ?? 0.2);
            const legWidth = getLShapeLegWidthBySpan(cabinetValues, spanMm);
            if (typeof legWidth === 'number') {
                cabinetLabel = legWidth;
            }
        }

        // Calculate arrow properties
        const ARROW_OFFSET = isSideWall ? 0 : 13;
        const ARROW_HEAD_LENGTH = 10;
        const ARROW_HEAD_WIDTH = 8;

        // Floor-distance length/label must match DistanceEditor / click.
        // The `height` argument can be a stale overlay size from a previous
        // cabinet (shared wall arrows) or a pre-seat AABB.
        const isFloorDistance = arrowGroupId === 'arrowGroupLeft-distance';
        const roomDims = isFloorDistance ? getRoomDimensionsComputed() : null;
        const floorClearanceScene = roomDims
            ? getFloorClearanceSceneUnits(cabinetGroup, roomDims.wallHeight)
            : 0;
        const floorClearanceMm = roomDims
            ? getFloorClearanceMm(
                  cabinetGroup,
                  roomDims.wallHeight,
                  roomDims.scale || scale || 0.2
              )
            : 0;

        // Calculate new arrow length based on width parameter
        // For manual panels, use thickness instead of height since they lay flat
        let newWidth = height ?? computedWidth;
        if (isFloorDistance) {
            newWidth = Math.max(0, floorClearanceScene);
        } else if ((isManualPanel || isUShapedPanel) && height) {
            const templateName = userData.templateName;
            const isExteriorPanel = templateName?.includes('Exterior');
            const thickness = isExteriorPanel
                ? Number(cabinetValues?.cabinet_ext_thickness)
                : Number(cabinetValues?.cabinet_carc_thickness);
            newWidth = thickness * (scale ?? 0.2);
        } else if (isRobeProduct && height) {
            const robeHeight = Number(cabinetValues?.cabinet_height) || 0;
            newWidth = robeHeight * (scale ?? 0.2);
        } else if (
            cabinetValues.type_name === 'Drawer Face' &&
            height &&
            arrowGroupId !== 'arrowGroupLeft-distance'
        ) {
            const panelLength =
                Number(cabinetValues?.cabinet_panel_length) || 0;
            newWidth = panelLength * (scale ?? 0.2);
        }

        const newArrowLength = newWidth;

        // Skip if length is zero or less
        if (newArrowLength <= 0) {
            arrowGroup.visible = false;
            return;
        }

        // Update arrows
        leftArrow.setLength(
            newArrowLength - ARROW_OFFSET,
            ARROW_HEAD_LENGTH,
            ARROW_HEAD_WIDTH
        );
        rightArrow.setLength(
            newArrowLength + ARROW_OFFSET,
            ARROW_HEAD_LENGTH,
            ARROW_HEAD_WIDTH
        );

        // Update arrow positions
        leftArrow.position.set(newWidth, 5, 40);
        rightArrow.position.set(ARROW_OFFSET, 5, 40);

        // Clean up existing text mesh
        arrowGroup.remove(textMesh);
        if (textMesh && textMesh.material) {
            textMesh.material.dispose();
        }

        // Calculate cabinet height
        const isLShapedProduct = userData.isLShapedProduct;
        const displayHeight = calculateDisplayHeight(
            cabinetGroup,
            cabinetValues
        );
        const panelHeight = isLShapedProduct
            ? cabinetValues.cabinet_carc_thickness || 0
            : 0;

        let cabinetHeight: string | number = 0;

        if (isFloorDistance) {
            cabinetHeight = floorClearanceMm;
        } else if (height) {
            if (isManualPanel) {
                // Manual panels: show thickness for cabinet's own height measurement
                const templateName = userData.templateName;
                const isExteriorPanel = templateName?.includes('Exterior');
                cabinetHeight = isExteriorPanel
                    ? Number(cabinetValues?.cabinet_ext_thickness)
                    : Number(cabinetValues?.cabinet_carc_thickness);
            } else if (isLShapedProduct && isPanelProduct) {
                cabinetHeight = panelHeight;
            } else if (isPanelProductWithLength) {
                cabinetHeight = cabinetValues?.cabinet_panel_width;
            } else if (isPanelProductWithWidth) {
                cabinetHeight = cabinetValues?.cabinet_panel_length;
            } else if (isUShapedPanel) {
                cabinetHeight = cabinetValues?.cabinet_carc_thickness;
            } else if (cabinetValues.type_name == 'Drawer Face') {
                cabinetHeight = Number(cabinetValues.cabinet_panel_length) || 0;
            } else if (userData.isPlaceholder) {
                // Prefer cabinet_height like normal cabinets.
                const fromCabinet = Number(cabinetValues?.cabinet_height);
                const actual = (
                    cabinetValues as unknown as {
                        actualDimensions?: {height?: number};
                    }
                )?.actualDimensions;
                cabinetHeight =
                    (Number.isFinite(fromCabinet) && fromCabinet > 0
                        ? fromCabinet
                        : actual?.height) ??
                    Math.round((height || 0) / (scale || 0.2));
            } else {
                cabinetHeight = displayHeight;
            }
        } else {
            cabinetHeight = cabinetLabel;
        }

        // Skip text creation if height is zero or less
        if (typeof cabinetHeight === 'number' && cabinetHeight <= 0) {
            arrowGroup.visible = false;
            return;
        }

        // Create new text mesh
        const textContent = String(cabinetHeight);
        const textColor =
            arrowGroupId === 'arrowGroupLeft-distance' ? '#333333' : '#000000';

        textMesh = createTextMesh(
            textContent,
            new THREE.Vector3(0, 0, 40),
            textColor
        );

        // Calculate text position using sprite scale
        const textWidth = 60; // Fixed width based on sprite scale

        const textPosition = calculateTextPosition(
            arrowGroupId,
            currentDirection,
            newWidth,
            textWidth,
            isSideWall,
            isLshapedRoom,
            cabinetHeight
        );

        // Apply positioning and rotations
        applyTextPositioning(
            textMesh,
            arrowGroupId,
            currentDirection,
            textPosition,
            isSideWall,
            cabinetHeight,
            isLshapedRoom
        );

        applyTextRotations(
            textMesh,
            isSideWall,
            isLshapedRoom,
            Boolean(cabinetGroup.userData.isLShapePanel)
        );

        // Add text mesh to group
        arrowGroup.add(textMesh);
        arrowGroup.userData.textMesh = textMesh;

        // Make arrowGroupLeft-distance text clickable for floor distance editing
        if (arrowGroup.userData.id === 'arrowGroupLeft-distance') {
            textMesh.userData.clickable = true;
            textMesh.userData.isTextSprite = true;
            textMesh.userData.measurementType = 'floor-distance';

            // Store original scale and color for hover state
            const originalScale = textMesh.scale.clone();
            const originalColor = textColor;

            textMesh.userData.onHover = () => {
                if (!arrowGroup.visible) {
                    return;
                }

                // Make text bigger on hover
                textMesh.scale.set(
                    originalScale.x * 1.6,
                    originalScale.y * 1.6,
                    originalScale.z
                );

                // Change color to black on hover
                const canvas = document.createElement('canvas');
                canvas.width = 384;
                canvas.height = 192;
                canvas.style.backgroundColor = 'transparent';

                const context = canvas.getContext('2d');
                if (!context) return;

                context.clearRect(0, 0, canvas.width, canvas.height);
                context.font = 'bold 120px Arial';
                context.textAlign = 'center';
                context.textBaseline = 'middle';
                context.fillStyle = '#333'; // Black color
                context.fillText(
                    textContent,
                    canvas.width / 2,
                    canvas.height / 2
                );

                const texture = new THREE.CanvasTexture(canvas);
                texture.needsUpdate = true;
                texture.premultiplyAlpha = true;

                if (textMesh.material.map) {
                    textMesh.material.map.dispose();
                }
                textMesh.material.map = texture;
                textMesh.material.needsUpdate = true;
            };

            textMesh.userData.onHoverEnd = () => {
                if (!arrowGroup.visible) {
                    return;
                }

                // Restore original scale
                textMesh.scale.copy(originalScale);

                // Restore original color
                const canvas = document.createElement('canvas');
                canvas.width = 384;
                canvas.height = 192;
                canvas.style.backgroundColor = 'transparent';

                const context = canvas.getContext('2d');
                if (!context) return;

                context.clearRect(0, 0, canvas.width, canvas.height);
                context.font = 'bold 100px Arial';
                context.textAlign = 'center';
                context.textBaseline = 'middle';
                context.fillStyle = originalColor;
                context.fillText(
                    textContent,
                    canvas.width / 2,
                    canvas.height / 2
                );

                const texture = new THREE.CanvasTexture(canvas);
                texture.needsUpdate = true;
                texture.premultiplyAlpha = true;

                if (textMesh.material.map) {
                    textMesh.material.map.dispose();
                }
                textMesh.material.map = texture;
                textMesh.material.needsUpdate = true;
            };

            textMesh.userData.onClick = (
                event: MouseEvent,
                raycaster: THREE.Raycaster,
                camera: THREE.Camera
            ) => {
                if (!arrowGroup.visible) {
                    return;
                }

                const worldPosition = textMesh.getWorldPosition(
                    new THREE.Vector3()
                );

                // Convert world position to screen coordinates
                const vector = worldPosition.clone();
                vector.project(camera);

                const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
                const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;

                // Same floor-clearance basis as useRoomLayout / DistanceEditor
                const roomDims = getRoomDimensionsComputed();
                const actualFloorDistance = getFloorClearanceMm(
                    cabinetGroup,
                    roomDims.wallHeight,
                    roomDims.scale || 0.2
                );

                // Trigger the floor distance editor display through the cabinet's userData
                if (
                    (cabinetGroup.userData as CabinetUserData).onDistanceClick
                ) {
                    (cabinetGroup.userData as CabinetUserData).onDistanceClick({
                        direction: 'floor-distance',
                        screenX: x,
                        screenY: y,
                        measurement: actualFloorDistance.toString(),
                        measurementObject: textMesh.parent,
                    });
                }
            };
        }
    };

    arrowGroup.userData.onDragEnd = () => {
        // Hide the entire group and all its children
        arrowGroup.visible = false;
        arrowGroup.traverse((child) => {
            child.visible = false;
        });
    };

    handleArrowsAndGroup([rightArrow, leftArrow], arrowGroup);

    // Hide the entire arrow group and all its children
    arrowGroup.visible = false;
};

export const createRoomDimensionArrow = ({
    arrowDir,
    reverseArrowDir,
    arrowOrigin,
    arrowLength,
    onArrowsCreated,
    isVertical = false,
}: {
    arrowDir: THREE.Vector3;
    reverseArrowDir: THREE.Vector3;
    arrowOrigin: THREE.Vector3;
    arrowLength: number;
    isVertical?: boolean;
    onArrowsCreated?: (
        arrow: THREE.ArrowHelper,
        reverseArrow: THREE.ArrowHelper
    ) => void;
}): THREE.Group<THREE.Object3DEventMap> => {
    const arrowHeadLength = 20;
    const arrowHeadWidth = 16;
    let reverserArrowOrigin = new THREE.Vector3(
        arrowOrigin.x + arrowLength,
        arrowOrigin.y,
        arrowOrigin.z
    );

    if (isVertical) {
        reverserArrowOrigin = new THREE.Vector3(
            arrowOrigin.x,
            arrowOrigin.y + arrowLength,
            arrowOrigin.z
        );
    }

    const arrow = new THREE.ArrowHelper(
        arrowDir,
        arrowOrigin,
        arrowLength,
        0x000000,
        arrowHeadLength,
        arrowHeadWidth
    );

    // Create reverse arrow for double-header effect (top)
    const reverseArrow = new THREE.ArrowHelper(
        reverseArrowDir,
        reverserArrowOrigin,
        arrowLength,
        0x000000,
        arrowHeadLength,
        arrowHeadWidth
    );

    onArrowsCreated && onArrowsCreated(arrow, reverseArrow);

    const arrowGroup = new THREE.Group();
    arrowGroup.add(arrow, reverseArrow);
    arrowGroup.name = 'dimension-arrows';

    return arrowGroup;
};

export const addCabinetDimensionText = (
    cabinetGroup: THREE.Group,
    handleArrowsAndGroup: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial | THREE.Mesh)[],
        arrowGroup?: THREE.Group<THREE.Object3DEventMap>
    ) => void
) => {
    const isPlaceholderProduct = Boolean(cabinetGroup.userData?.isPlaceholder);

    // Get cabinet dimensions. Cabinets are laid flat (rotation.x = -90°) so
    // world Z ≈ width and world X ≈ depth. Placeholders stand upright, so
    // world X ≈ width and world Z ≈ depth. Mesh-only bounds keep overlay
    // children from inflating the size into multi-million-mm labels.
    const box = isPlaceholderProduct
        ? computeWorldBoundsExcludingAnimatedDoorParts(cabinetGroup)
        : new THREE.Box3().setFromObject(cabinetGroup);
    const size = new THREE.Vector3();
    box.getSize(size);
    const width = isPlaceholderProduct ? size.x : size.z;
    let height = size.y;
    const depth = isPlaceholderProduct ? size.z : size.x;
    const userData = cabinetGroup.userData as UserData;
    const {
        isFillerProduct,
        isPanelProduct,
        isVerminProduct,
        isPanelProductWithLength,
        isPanelProductWithWidth,
        isManualPanel,
        templateName,
        isUShapedPanel,
    } = userData;
    const values = cabinetGroup.userData
        .values as RoomPlannerProductWithTemplate;
    const roomScale =
        getRoomDimensionsComputed().scale ||
        Number(
            (
                values as RoomPlannerProductWithTemplate & {
                    scale?: number;
                }
            )?.scale
        ) ||
        1;

    // For manual panels, use thickness instead of height since they lay flat
    if (isManualPanel || isUShapedPanel) {
        const isExteriorPanel = templateName?.includes('Exterior');
        const thickness = isExteriorPanel
            ? Number(values?.cabinet_ext_thickness)
            : Number(values?.cabinet_carc_thickness);
        height = thickness;
    }

    // Create a new group for the dimension indicators
    const dimensionGroup = new THREE.Group();
    dimensionGroup.name = 'cabinetDimensionArrows';

    // Add a unique ID to identify this arrow group
    dimensionGroup.userData.id = userData.id || 'cabinetDimensionArrows';

    // Store references to the width, depth, and height groups for updates
    dimensionGroup.userData.widthGroup = null;
    dimensionGroup.userData.depthGroup = null;
    dimensionGroup.userData.heightGroup = null;

    // Store all arrow objects and text materials to pass to the handler
    const arrowObjects: (
        THREE.ArrowHelper | THREE.SpriteMaterial | THREE.Mesh
    )[] = [];

    // Helper function to calculate dimension label position based on cabinet type
    const getDimensionLabelOffset = () => {
        if (isFillerProduct || isManualPanel) {
            return 0;
        }
        return -20;
    };

    // Helper function to format dimension values for cabinet labels
    const formatDimensionValue = (value: number): string => {
        return `${value}`; // Show exact decimal value
    };

    type PlaceholderActualDimensions = {
        width: number;
        height: number;
        depth: number;
    };

    // Scene units → mm (placeholders use PLACEHOLDER_SCENE_SCALE = 0.2)
    const PLACEHOLDER_TO_MM = 5;

    // Always use current world AABB → mm so labels match arrow lengths after rotate.
    // (Stored actualDimensions are local-axis sizes and swap under Y rotation.)
    const getPlaceholderDisplayDimensions = (
        fallbackWidth = width,
        fallbackDepth = depth,
        fallbackHeight = height
    ): PlaceholderActualDimensions => {
        return {
            width: Math.round(fallbackWidth * PLACEHOLDER_TO_MM),
            height: Math.round(fallbackHeight * PLACEHOLDER_TO_MM),
            depth: Math.round(fallbackDepth * PLACEHOLDER_TO_MM),
        };
    };

    const calculateWidthLabelPosition = (
        width: number,
        depth: number,
        height: number
    ) => {
        // Placeholders share the cabinet local frame via
        // syncOverlayTransformToCabinet — same mid-width / front-edge / near-top
        // placement as floor cabinets (no upright-pivot hacks).
        const calculatedWidth = width / 2;

        // Standard cabinet positioning with product-specific adjustments
        let heightOffset = 20;
        if (isFillerProduct || isPanelProduct) {
            if (isVerminProduct) {
                heightOffset = height - 20;
            } else {
                heightOffset = 0;
            }
        } else if (isUShapedPanel) {
            heightOffset = height;
        }

        return new THREE.Vector3(calculatedWidth, depth, height - heightOffset);
    };

    const calculateDepthLabelPosition = (
        width: number,
        depth: number,
        height: number
    ) => {
        // L-shaped cabinets place the depth label on the left edge, not right.
        let xPosition = width;
        if (cabinetGroup.userData.isLShapePanel) {
            xPosition = -5;
        } else if (cabinetGroup.userData.isLShapedProduct) {
            xPosition = 0;
        }
        const heightOffset = isVerminProduct || isUShapedPanel ? height : 20;
        const depthPosition = depth / 2;

        return new THREE.Vector3(
            xPosition,
            depthPosition,
            height - heightOffset
        );
    };

    const getScaledLShapedLeftWidthSpan = () => {
        const leftWidthMm =
            Number(
                (
                    values as RoomPlannerProductWithTemplate & {
                        width2?: number;
                    }
                )?.cabinet_left_width ??
                    (
                        values as RoomPlannerProductWithTemplate & {
                            width2?: number;
                        }
                    )?.width2 ??
                    values?.cabinet_width
            ) || 0;

        return leftWidthMm > 0 ? leftWidthMm * roomScale : depth;
    };

    const getScaledLShapedRightWidthSpan = () => {
        const rightWidthMm =
            Number(
                (
                    values as RoomPlannerProductWithTemplate & {
                        width?: number;
                    }
                )?.cabinet_right_width ??
                    (
                        values as RoomPlannerProductWithTemplate & {
                            width?: number;
                        }
                    )?.width ??
                    values?.cabinet_width
            ) || 0;

        return rightWidthMm > 0 ? rightWidthMm * roomScale : width;
    };

    const getScaledLShapedLeftDepthSpan = () => {
        const leftDepthMm =
            Number(
                (
                    values as RoomPlannerProductWithTemplate & {
                        cabinet_width_depth?: number;
                        width_depth?: number;
                    }
                )?.cabinet_left_depth ??
                    (
                        values as RoomPlannerProductWithTemplate & {
                            cabinet_width_depth?: number;
                            width_depth?: number;
                        }
                    )?.cabinet_width_depth ??
                    (
                        values as RoomPlannerProductWithTemplate & {
                            cabinet_width_depth?: number;
                            width_depth?: number;
                        }
                    )?.width_depth
            ) || 0;

        return leftDepthMm > 0 ? leftDepthMm * roomScale : width;
    };

    const getScaledLShapedOppositeDepthSpan = () => {
        const oppositeDepthMm =
            Number(
                (
                    values as RoomPlannerProductWithTemplate & {
                        cabinet_length_depth?: number;
                        length_depth?: number;
                    }
                )?.cabinet_right_depth ??
                    (
                        values as RoomPlannerProductWithTemplate & {
                            cabinet_length_depth?: number;
                            length_depth?: number;
                        }
                    )?.cabinet_length_depth ??
                    (
                        values as RoomPlannerProductWithTemplate & {
                            cabinet_length_depth?: number;
                            length_depth?: number;
                        }
                    )?.length_depth
            ) || 0;

        return oppositeDepthMm > 0 ? oppositeDepthMm * roomScale : depth;
    };

    const getScaledLShapedRightDepthCenterY = (
        totalDepthSpan = getScaledLShapedLeftWidthSpan()
    ) => {
        const rightDepthSpan = getScaledLShapedOppositeDepthSpan();

        if (rightDepthSpan > 0 && rightDepthSpan < totalDepthSpan) {
            return totalDepthSpan - rightDepthSpan / 2;
        }

        return totalDepthSpan / 2;
    };

    const createDimensionAnchor = (
        name: string,
        position: THREE.Vector3
    ): THREE.Group<THREE.Object3DEventMap> => {
        const anchor = new THREE.Group();
        anchor.name = name;
        anchor.position.copy(position);

        return anchor;
    };

    let offset = getDimensionLabelOffset();
    const createWidthDimension = (
        width: number,
        depth: number,
        height: number
    ) => {
        // Remove existing width group if any
        if (dimensionGroup.userData.widthGroup) {
            dimensionGroup.remove(
                dimensionGroup.userData.widthGroup as THREE.Object3D
            );
        }

        // Width direction - Calculate positions using helper function
        const centerPos = calculateWidthLabelPosition(width, depth, height);

        // Create width group
        const widthGroup = new THREE.Group();
        widthGroup.name = 'width-dimension';
        const widthUserData = widthGroup.userData as {
            textSprite?: THREE.Sprite;
            panelTextSprite?: THREE.Sprite;
            panelTextAnchor?: THREE.Group<THREE.Object3DEventMap>;
        };

        let displayWidth = 0;
        // Placeholders use cabinet_width like floor cabinets.
        if (cabinetGroup.userData.isPlaceholder) {
            displayWidth =
                Number(values?.cabinet_width) ||
                getPlaceholderDisplayDimensions().width;
        } else if (isPanelProduct) {
            displayWidth = Number(values?.cabinet_ext_thickness) || 0;
        } else if (cabinetGroup.userData.isLShapePanel) {
            displayWidth = Number(values?.cabinet_panel_length) || 0;
        } else if (cabinetGroup.userData.isLShapedProduct) {
            displayWidth =
                Number(values?.cabinet_right_width || values?.cabinet_width) ||
                0;
        } else if (isPanelProductWithLength) {
            displayWidth = Number(values?.cabinet_carc_thickness) || 0;
        } else if (isManualPanel) {
            // Manual panels: form Width field maps to cabinet_panel_width
            displayWidth =
                Number(values?.cabinet_panel_width || values?.cabinet_width) ||
                0;
        } else if (isPanelProductWithWidth) {
            displayWidth = Number(values?.cabinet_carc_thickness) || 0;
        } else if (isUShapedPanel) {
            displayWidth = Number(values?.cabinet_panel_length) || 0;
        } else if (values.type_name == 'Drawer Face') {
            displayWidth = Number(values.cabinet_panel_width) || 0;
        } else {
            displayWidth = Number(values?.cabinet_width) || 0;
        }

        // Create text for the dimension
        // Manual panels, panel products, and placeholders show exact decimal values for thickness/carcase
        const displayText =
            isPanelProduct ||
            isPanelProductWithLength ||
            isPanelProductWithWidth ||
            isManualPanel ||
            cabinetGroup.userData.isPlaceholder
                ? `${displayWidth}`
                : formatDimensionValue(displayWidth);

        // Create sprite for text
        const createTextSprite = (text: string) => {
            // Create canvas - larger canvas to prevent text cropping
            const canvas = document.createElement('canvas');
            canvas.width = 384; // Increased from 256
            canvas.height = 192; // Increased from 128
            canvas.style.backgroundColor = 'transparent';

            const context = canvas.getContext('2d');
            if (!context) return null;

            // Clear the canvas to make it transparent
            context.clearRect(0, 0, canvas.width, canvas.height);

            // Text - larger and bolder for better visibility
            context.font = 'bold 100px Arial'; // Increased from 40px
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillStyle = 'black';

            // Add padding around text to prevent cropping
            context.fillText(text, canvas.width / 2, canvas.height / 2);

            // Create texture
            const texture = new THREE.CanvasTexture(canvas);
            texture.needsUpdate = true;
            texture.premultiplyAlpha = true;

            // Create sprite material
            const spriteMaterial = new THREE.SpriteMaterial({
                map: texture,
                transparent: true,
                depthTest: false,
                depthWrite: false,
                alphaTest: 0.01,
                sizeAttenuation: false,
            });

            // Create sprite - larger scale
            const sprite = new THREE.Sprite(spriteMaterial);
            sprite.scale.set(60, 30, 1); // Increased from 40, 20, 1

            // Ensure sprite always renders on top
            sprite.renderOrder = 999;

            return sprite;
        };

        // Create the text sprite
        const textSprite = createTextSprite(displayText);
        if (textSprite) {
            textSprite.position.copy(centerPos);

            // Hide text when isFillerProduct is true and isVerminProduct is false

            const shouldHideText = isFillerProduct && !isVerminProduct;
            textSprite.visible = !shouldHideText;

            if (!isFillerProduct || isVerminProduct) {
                widthGroup.add(textSprite);
            }

            widthGroup.userData.textSprite = textSprite;
        }

        // Second width label: panels use cabinet_width_depth, L-shapes mirror
        // the two-label style with cabinet_left_depth.
        if (
            cabinetGroup.userData.isLShapePanel ||
            cabinetGroup.userData.isLShapedProduct
        ) {
            const secondWidth = cabinetGroup.userData.isLShapePanel
                ? (
                      cabinetGroup.userData.values as {
                          cabinet_width_depth?: number;
                      }
                  )?.cabinet_width_depth || 0
                : Number(values?.cabinet_left_depth) || 0;
            const panelText = formatDimensionValue(secondWidth);

            const panelTextSprite = createTextSprite(panelText);
            if (panelTextSprite) {
                // L-shapes pin the label to the left-leg center at height - 20;
                // a literal (not `offset`) avoids drift when createDepthDimension
                // later zeroes `offset`. Panels keep their front position.
                if (cabinetGroup.userData.isLShapedProduct) {
                    const leftDepthAnchor = createDimensionAnchor(
                        'left-depth-width-anchor',
                        new THREE.Vector3(
                            getScaledLShapedLeftDepthSpan() / 2,
                            0,
                            height - 20
                        )
                    );
                    leftDepthAnchor.add(panelTextSprite);
                    widthGroup.add(leftDepthAnchor);
                    widthUserData.panelTextAnchor = leftDepthAnchor;
                } else {
                    const oppositePos = new THREE.Vector3(
                        width / 4,
                        depth,
                        height + offset
                    );
                    panelTextSprite.position.copy(oppositePos);
                    widthGroup.add(panelTextSprite);
                }
                widthUserData.panelTextSprite = panelTextSprite;
            }
        }

        // Create a position update method
        widthGroup.userData.updatePosition = (
            newWidth: number,
            newDepth: number,
            newHeight: number
        ) => {
            // Calculate new position using helper function
            const newCenterPos = calculateWidthLabelPosition(
                newWidth,
                newDepth,
                newHeight
            );

            // Update sprite position
            if (widthUserData.textSprite) {
                const textSprite = widthUserData.textSprite;
                textSprite.position.copy(newCenterPos);
            }

            // Mirror the creation placement so the label does not drift on
            // re-render (left-leg center, fixed height - 20).
            if (widthUserData.panelTextSprite) {
                if (
                    cabinetGroup.userData.isLShapedProduct &&
                    widthUserData.panelTextAnchor
                ) {
                    widthUserData.panelTextAnchor.position.set(
                        getScaledLShapedLeftDepthSpan() / 2,
                        0,
                        newHeight - 20
                    );
                } else {
                    const panelTextSprite = widthUserData.panelTextSprite;
                    const newLeftLegCenterX = cabinetGroup.userData
                        .isLShapedProduct
                        ? newWidth / 3
                        : newWidth / 4;
                    const newOppositePos = new THREE.Vector3(
                        newLeftLegCenterX,
                        0,
                        newHeight - 20
                    );
                    panelTextSprite.position.copy(newOppositePos);
                }
            }
        };

        // Add a method to update the text value
        widthGroup.userData.updateText = (newWidth: number) => {
            // Handle placeholder dimensions
            let displayWidth = newWidth;
            if (cabinetGroup.userData.isPlaceholder) {
                displayWidth = getPlaceholderDisplayDimensions(
                    newWidth,
                    depth,
                    height
                ).width;
            } else if (isManualPanel) {
                // For manual panels, use form values instead of bounding box dimensions
                displayWidth =
                    Number(
                        values?.cabinet_panel_width || values?.cabinet_width
                    ) || 0;
            } else if (cabinetGroup.userData.isLShapedProduct) {
                displayWidth =
                    Number(
                        values?.cabinet_right_width || values?.cabinet_width
                    ) || newWidth;
            }
            // Manual panels and placeholders show exact decimal values, others use formatDimensionValue
            const displayText =
                isManualPanel || cabinetGroup.userData.isPlaceholder
                    ? `${displayWidth}`
                    : formatDimensionValue(displayWidth);
            const textSprite = createTextSprite(displayText);

            if (textSprite && widthUserData.textSprite) {
                // Replace old sprite with new one
                const oldSprite = widthUserData.textSprite;
                textSprite.position.copy(oldSprite.position);

                // Apply visibility logic to the new sprite

                widthGroup.remove(oldSprite);
                widthGroup.add(textSprite);
                widthUserData.textSprite = textSprite;
            }

            // Update duplicate width sprite for L-shaped panels and cabinets
            if (
                !cabinetGroup.userData.isLShapePanel &&
                !cabinetGroup.userData.isLShapedProduct
            ) {
                if (widthUserData.panelTextSprite) {
                    const oldPanelSprite = widthUserData.panelTextSprite;
                    if (widthUserData.panelTextAnchor) {
                        widthUserData.panelTextAnchor.remove(oldPanelSprite);
                        widthGroup.remove(widthUserData.panelTextAnchor);
                        widthUserData.panelTextAnchor = undefined;
                    } else {
                        widthGroup.remove(oldPanelSprite);
                    }
                    widthUserData.panelTextSprite = undefined;
                }
            } else {
                const secondWidth = cabinetGroup.userData.isLShapePanel
                    ? (
                          cabinetGroup.userData.values as {
                              cabinet_panel_length?: number;
                          }
                      )?.cabinet_panel_length || 0
                    : Number(values?.cabinet_left_depth) || 0;
                const newPanelTextSprite = createTextSprite(
                    formatDimensionValue(secondWidth)
                );
                if (newPanelTextSprite) {
                    if (widthUserData.panelTextSprite) {
                        const oldPanelSprite = widthUserData.panelTextSprite;
                        newPanelTextSprite.position.copy(
                            oldPanelSprite.position
                        );
                        if (widthUserData.panelTextAnchor) {
                            newPanelTextSprite.rotation.copy(
                                oldPanelSprite.rotation
                            );
                            widthUserData.panelTextAnchor.remove(
                                oldPanelSprite
                            );
                            widthUserData.panelTextAnchor.add(
                                newPanelTextSprite
                            );
                        } else {
                            widthGroup.remove(oldPanelSprite);
                        }
                    } else {
                        // Place at a separate position from the main label
                        if (cabinetGroup.userData.isLShapedProduct) {
                            const leftDepthAnchor = createDimensionAnchor(
                                'left-depth-width-anchor',
                                new THREE.Vector3(
                                    getScaledLShapedLeftDepthSpan() / 2,
                                    0,
                                    height - 20
                                )
                            );
                            leftDepthAnchor.add(newPanelTextSprite);
                            widthGroup.add(leftDepthAnchor);
                            widthUserData.panelTextAnchor = leftDepthAnchor;
                        } else {
                            const fallbackOppositePos = new THREE.Vector3(
                                width / 2,
                                depth / 2,
                                height + offset
                            );
                            newPanelTextSprite.position.copy(
                                fallbackOppositePos
                            );
                        }
                    }
                    if (!widthUserData.panelTextAnchor) {
                        widthGroup.add(newPanelTextSprite);
                    }
                    widthUserData.panelTextSprite = newPanelTextSprite;
                }
            }
        };

        // Add width group to dimension group
        dimensionGroup.add(widthGroup);

        // Store reference to width group for updates
        dimensionGroup.userData.widthGroup = widthGroup;
        return widthGroup;
    };

    // Create depth dimension arrows and update function
    const createDepthDimension = (
        width: number,
        depth: number,
        height: number
    ) => {
        // Remove existing depth group if any
        if (dimensionGroup.userData.depthGroup) {
            dimensionGroup.remove(
                dimensionGroup.userData.depthGroup as THREE.Object3D
            );
        }

        // Depth direction (back to front) - Calculate positions using helper function
        const centerPos = calculateDepthLabelPosition(width, depth, height);
        offset = isFillerProduct ? -20 : 0;
        // Create depth group
        const depthGroup = new THREE.Group();
        depthGroup.name = 'depth-dimension';
        const depthUserData = depthGroup.userData as {
            textSprite?: THREE.Sprite;
            textAnchor?: THREE.Group<THREE.Object3DEventMap>;
            oppositeTextSprite?: THREE.Sprite;
            oppositeTextAnchor?: THREE.Group<THREE.Object3DEventMap>;
        };
        const shouldUseLShapeDepthAnchors =
            !!cabinetGroup.userData.isLShapedProduct;

        let displayDepth = 0;
        // Placeholders use cabinet_depth like floor cabinets.
        if (cabinetGroup.userData.isPlaceholder) {
            displayDepth =
                Number(values?.cabinet_depth) ||
                getPlaceholderDisplayDimensions().depth;
        } else if (isPanelProduct) {
            displayDepth = Number(values.cabinet_applied_panel_depth) || 0;
        } else if (cabinetGroup.userData.isVerminProduct) {
            if (cabinetGroup.userData.isUpperProduct) {
                displayDepth = Number(values.cabinet_upper_filler_depth) || 0;
            } else {
                displayDepth = Number(values.cabinet_depth) || 0;
            }
        } else if (cabinetGroup.userData.isFillerProduct) {
            displayDepth = Number(values.cabinet_width) || 0;
        } else if (cabinetGroup.userData.isLShapedProduct) {
            displayDepth =
                Number(values.cabinet_left_width || values.cabinet_width) || 0;
        } else if (isPanelProductWithLength) {
            displayDepth = Number(values.cabinet_panel_length) || 0;
        } else if (isManualPanel) {
            // Manual panels: form Height field maps to cabinet_panel_length
            displayDepth = Number(values.cabinet_panel_length) || 0;
        } else if (isPanelProductWithWidth || isUShapedPanel) {
            displayDepth = Number(values.cabinet_panel_width) || 0;
        } else {
            displayDepth = Number(values.cabinet_depth) || 0;
        }
        // Create text for the dimension
        // Manual panels and placeholders show exact decimal values for depth/width measurements
        const displayText =
            isManualPanel || cabinetGroup.userData.isPlaceholder
                ? `${displayDepth}`
                : formatDimensionValue(displayDepth);

        // Create sprite for text
        const createTextSprite = (text: string) => {
            // Create canvas - larger canvas to prevent text cropping
            const canvas = document.createElement('canvas');
            canvas.width = 384; // Increased from 256
            canvas.height = 192; // Increased from 128
            canvas.style.backgroundColor = 'transparent';

            const context = canvas.getContext('2d');
            if (!context) return null;

            // Clear the canvas to make it transparent
            context.clearRect(0, 0, canvas.width, canvas.height);

            // Text - larger and bolder for better visibility
            context.font = 'bold 100px Arial'; // Increased from 40px
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillStyle = 'black';

            // Add padding around text to prevent cropping
            context.fillText(text, canvas.width / 2, canvas.height / 2);

            // Create texture
            const texture = new THREE.CanvasTexture(canvas);
            texture.needsUpdate = true;
            texture.premultiplyAlpha = true;

            // Create sprite material
            const spriteMaterial = new THREE.SpriteMaterial({
                map: texture,
                transparent: true,
                depthTest: false,
                depthWrite: false,
                alphaTest: 0.01,
                sizeAttenuation: false,
            });

            // Create sprite - larger scale
            const sprite = new THREE.Sprite(spriteMaterial);

            sprite.scale.set(60, 30, 1); // Increased from 40, 20, 1

            // Ensure sprite always renders on top
            sprite.renderOrder = 999;

            return sprite;
        };

        // Create the text sprite
        if (values.type_name !== 'Drawer Face') {
            const textSprite = createTextSprite(displayText);
            if (textSprite) {
                if (shouldUseLShapeDepthAnchors) {
                    const leftDepthAnchor = createDimensionAnchor(
                        'left-depth-anchor',
                        new THREE.Vector3(
                            0,
                            getScaledLShapedLeftWidthSpan() / 2,
                            centerPos.z
                        )
                    );
                    leftDepthAnchor.add(textSprite);
                    depthGroup.add(leftDepthAnchor);
                    depthUserData.textAnchor = leftDepthAnchor;
                } else {
                    textSprite.position.copy(centerPos);
                    depthGroup.add(textSprite);
                }
                depthUserData.textSprite = textSprite;
            }
        }

        // Opposite-side depth sprite: panels use cabinet_length_depth, L-shapes
        // mirror it with cabinet_right_depth.
        if (
            cabinetGroup.userData.isLShapePanel ||
            cabinetGroup.userData.isLShapedProduct
        ) {
            const oppositeDisplayDepth = cabinetGroup.userData.isLShapePanel
                ? (
                      cabinetGroup.userData.values as {
                          cabinet_length_depth?: number;
                      }
                  )?.cabinet_length_depth || 0
                : Number(values?.cabinet_right_depth) || 0;
            const oppositeDisplayText =
                formatDimensionValue(oppositeDisplayDepth);

            const oppositeTextSprite = createTextSprite(oppositeDisplayText);
            if (oppositeTextSprite) {
                if (shouldUseLShapeDepthAnchors) {
                    const rightDepthAnchor = createDimensionAnchor(
                        'right-depth-anchor',
                        new THREE.Vector3(
                            getScaledLShapedRightWidthSpan(),
                            getScaledLShapedRightDepthCenterY(),
                            height - 20
                        )
                    );
                    rightDepthAnchor.add(oppositeTextSprite);
                    depthGroup.add(rightDepthAnchor);
                    depthUserData.oppositeTextAnchor = rightDepthAnchor;
                } else {
                    // Panels keep their existing cabinet-relative placement.
                    const cabinetDepthMm = Number(values?.cabinet_depth) || 0;
                    const rightDepthScaled =
                        cabinetDepthMm > 0
                            ? (oppositeDisplayDepth * depth) / cabinetDepthMm
                            : depth;
                    const oppositePos = new THREE.Vector3(
                        width,
                        rightDepthScaled / 2,
                        height - 20
                    );
                    oppositeTextSprite.position.copy(oppositePos);
                    depthGroup.add(oppositeTextSprite);
                }
                depthUserData.oppositeTextSprite = oppositeTextSprite;
            }
            rotateAroundCenter(oppositeTextSprite, Math.PI);
        }

        // Create a position update method
        depthGroup.userData.updatePosition = (
            newWidth: number,
            newDepth: number,
            newHeight: number
        ) => {
            // Calculate new position using helper function
            const newCenterPos = calculateDepthLabelPosition(
                newWidth,
                newDepth,
                newHeight
            );

            // Update sprite position
            if (depthUserData.textAnchor) {
                depthUserData.textAnchor.position.set(
                    0,
                    getScaledLShapedLeftWidthSpan() / 2,
                    newCenterPos.z
                );
            } else if (depthUserData.textSprite) {
                depthUserData.textSprite.position.copy(newCenterPos);
            }

            // Mirror the initial-render placement so the label does not drift.
            if (depthUserData.oppositeTextAnchor) {
                depthUserData.oppositeTextAnchor.position.set(
                    getScaledLShapedRightWidthSpan(),
                    getScaledLShapedRightDepthCenterY(newDepth),
                    newHeight - 20
                );
            } else if (depthUserData.oppositeTextSprite) {
                const cabinetDepthMm = Number(values?.cabinet_depth) || 0;
                const rightDepthMm = Number(values?.cabinet_right_depth) || 0;
                const newRightDepthScaled =
                    cabinetDepthMm > 0
                        ? (rightDepthMm * newDepth) / cabinetDepthMm
                        : newDepth;
                const newOppositePos = new THREE.Vector3(
                    newWidth,
                    newRightDepthScaled / 2,
                    newHeight - 20
                );
                depthUserData.oppositeTextSprite.position.copy(newOppositePos);
            }
        };

        // Add a method to update the text value
        depthGroup.userData.updateText = (newDepth: number) => {
            // Handle placeholder dimensions
            let displayDepth = newDepth;
            if (cabinetGroup.userData.isPlaceholder) {
                displayDepth = getPlaceholderDisplayDimensions(
                    width,
                    newDepth,
                    height
                ).depth;
            } else if (isManualPanel) {
                // For manual panels, use form values instead of bounding box dimensions
                displayDepth = Number(values?.cabinet_panel_length) || 0;
            } else if (cabinetGroup.userData.isLShapedProduct) {
                displayDepth =
                    Number(
                        values?.cabinet_left_width || values?.cabinet_width
                    ) || newDepth;
            }
            // Manual panels and placeholders show exact decimal values, others use formatDimensionValue
            const displayText =
                isManualPanel || cabinetGroup.userData.isPlaceholder
                    ? `${displayDepth}`
                    : formatDimensionValue(displayDepth);
            const textSprite = createTextSprite(displayText);

            if (textSprite && depthUserData.textSprite) {
                // Replace old sprite with new one
                const oldSprite = depthUserData.textSprite;
                textSprite.position.copy(oldSprite.position);
                textSprite.rotation.copy(oldSprite.rotation);
                if (depthUserData.textAnchor) {
                    depthUserData.textAnchor.remove(oldSprite);
                    depthUserData.textAnchor.add(textSprite);
                } else {
                    depthGroup.remove(oldSprite);
                    depthGroup.add(textSprite);
                }
                depthUserData.textSprite = textSprite;
            }

            // Update opposite-side sprite text for L-shaped panels and cabinets
            if (depthUserData.oppositeTextSprite) {
                if (
                    !cabinetGroup.userData.isLShapePanel &&
                    !cabinetGroup.userData.isLShapedProduct
                ) {
                    const oldOppositeSprite = depthUserData.oppositeTextSprite;
                    if (depthUserData.oppositeTextAnchor) {
                        depthUserData.oppositeTextAnchor.remove(
                            oldOppositeSprite
                        );
                        depthGroup.remove(depthUserData.oppositeTextAnchor);
                        depthUserData.oppositeTextAnchor = undefined;
                    } else {
                        depthGroup.remove(oldOppositeSprite);
                    }
                    depthUserData.oppositeTextSprite = undefined;
                } else {
                    const oppositeDisplayDepth = cabinetGroup.userData
                        .isLShapePanel
                        ? (
                              cabinetGroup.userData.values as {
                                  cabinet_length_depth?: number;
                              }
                          )?.cabinet_length_depth || 0
                        : Number(values?.cabinet_right_depth) || 0;
                    const newOppositeTextSprite = createTextSprite(
                        formatDimensionValue(oppositeDisplayDepth)
                    );
                    if (newOppositeTextSprite) {
                        const oldOppositeSprite =
                            depthUserData.oppositeTextSprite;
                        newOppositeTextSprite.position.copy(
                            oldOppositeSprite.position
                        );
                        newOppositeTextSprite.rotation.copy(
                            oldOppositeSprite.rotation
                        );
                        if (depthUserData.oppositeTextAnchor) {
                            depthUserData.oppositeTextAnchor.remove(
                                oldOppositeSprite
                            );
                            depthUserData.oppositeTextAnchor.add(
                                newOppositeTextSprite
                            );
                        } else {
                            depthGroup.remove(oldOppositeSprite);
                            depthGroup.add(newOppositeTextSprite);
                        }
                        depthUserData.oppositeTextSprite =
                            newOppositeTextSprite;
                    }
                }
            }
        };

        // Add depth group to dimension group
        dimensionGroup.add(depthGroup);

        // Store reference to depth group for updates
        dimensionGroup.userData.depthGroup = depthGroup;

        return depthGroup;
    };

    // Create a function to update all dimensions
    dimensionGroup.userData.updateDimensions = (
        newWidth: number,
        newDepth: number,
        newHeight: number
    ) => {
        // First, clear the arrowObjects array to avoid duplication
        arrowObjects.length = 0;
        // Then use the interface in your conditionals
        if (dimensionGroup.userData.widthGroup) {
            (
                dimensionGroup.userData.widthGroup as GroupWithUpdatePosition
            ).userData.updatePosition(newWidth, newDepth, newHeight);
        }

        if (dimensionGroup.userData.heightGroup) {
            (
                dimensionGroup.userData.heightGroup as GroupWithUpdatePosition
            ).userData.updatePosition(newWidth, newDepth, newHeight);
        }

        if (dimensionGroup.userData.depthGroup) {
            (
                dimensionGroup.userData.depthGroup as GroupWithUpdatePosition
            ).userData.updatePosition(newWidth, newDepth, newHeight);
        } else {
            createDepthDimension(newWidth, newDepth, newHeight);
        }

        // Re-register with arrow handler
        handleArrowsAndGroup(arrowObjects, dimensionGroup);
    };

    dimensionGroup.userData.dispose = () => {
        // Remove from parent if added to parent instead of cabinet
        if (dimensionGroup.parent) {
            dimensionGroup.parent.remove(dimensionGroup);
        }
    };

    cabinetGroup.userData.disposeDimensionArrows = () => {
        (
            dimensionGroup.userData as {
                dispose: () => void;
            }
        ).dispose();
    };

    // Normal orientation for other directions
    createWidthDimension(width, depth, height);
    createDepthDimension(width, depth, height);

    // Add dimension group to the scene at the cabinet's position
    if (cabinetGroup.parent) {
        cabinetGroup.parent.add(dimensionGroup);
    } else {
        return null;
    }
    // Cabinets: copy pose. Placeholders: map upright GLB into cabinet local frame
    // so width/depth labels sit on the mesh edges like floor cabinets.
    syncOverlayTransformToCabinet(dimensionGroup, cabinetGroup);
    // Set initial visibility to false
    dimensionGroup.visible = false;
    // Add onDrag and onDragEnd handlers
    const cabinetUserData = (cabinetGroup as CabinetMesh).userData;
    const previousOverlapWhileDragging =
        cabinetUserData.onCabinetOverlapsWhileDragging as
            ((overlapping?: boolean) => void) | undefined;
    cabinetUserData.onCabinetOverlapsWhileDragging = (
        overlapping?: boolean
    ) => {
        if (overlapping === true) {
            dimensionGroup.visible = false;
            dimensionGroup.traverse((child) => {
                child.visible = false;
            });
        }
        if (previousOverlapWhileDragging !== undefined) {
            previousOverlapWhileDragging(overlapping);
        }
    };

    dimensionGroup.userData.onDrag = (
        width: number,
        depth: number,
        height: number
    ) => {
        // Visibility is driven by useRoomLayout / calculateArrowVisibility — do not force visible here
        if (width && depth && height) {
            // Placeholders store facing on rotation.y; cabinets use rotation.z
            const facingRotation = isPlaceholderProduct
                ? cabinetGroup.rotation.y
                : cabinetGroup.rotation.z;
            const direction = getDirection(facingRotation);
            const isHorizontal = direction === 'left' || direction === 'right';

            if (!isHorizontal)
                (
                    dimensionGroup.userData as {
                        updateDimensions: (
                            width: number,
                            depth: number,
                            height: number
                        ) => void;
                    }
                ).updateDimensions(width, depth, height);
            else
                (
                    dimensionGroup.userData as {
                        updateDimensions: (
                            depth: number,
                            width: number,
                            height: number
                        ) => void;
                    }
                ).updateDimensions(width, depth, height);
        }
    };

    dimensionGroup.userData.onDragEnd = () => {
        dimensionGroup.visible = false;
    };
    // Register with arrow handler
    handleArrowsAndGroup(arrowObjects, dimensionGroup);
    return dimensionGroup;
};

export const addHeightArrowsAndText = (
    wall: THREE.Mesh,
    handleArrows: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial)[]
    ) => void,
    wallHeight: number,
    scale = 1,
    id = '',
    defaultLabelFontSize: string
) => {
    const arrowsAndText = [];
    const arrowColor = '#A9A9A9';
    // Height Arrow Helper
    const heightArrowDir = new THREE.Vector3(0, 1, 0).normalize();
    const heightArrowLength = wallHeight;

    // Create bottom arrow (height)
    const bottomArrow = new THREE.ArrowHelper(
        heightArrowDir.clone().negate(),
        new THREE.Vector3(-5, wallHeight, 12.5),
        heightArrowLength,
        arrowColor,
        8,
        8
    );

    // Create top arrow (height)
    const topArrow = new THREE.ArrowHelper(
        heightArrowDir,
        new THREE.Vector3(-5, 0, 12.5),
        heightArrowLength,
        arrowColor,
        8,
        8
    );
    wall.add(topArrow);

    // Text for height
    const heightCanvas = document.createElement('canvas');
    heightCanvas.width = 156;
    heightCanvas.height = 60;
    const heightContext = heightCanvas.getContext('2d');

    heightContext.font = defaultLabelFontSize;
    heightContext.fillStyle = '#000';
    heightContext.textAlign = 'center';
    heightContext.textBaseline = 'middle';

    // Calculate actual height in mm based on scale
    const heightInMm = Math.round(wallHeight / scale);
    heightContext.fillText(
        `${heightInMm}`,
        heightCanvas.width / 2,
        heightCanvas.height / 2
    );

    const heightTexture = new THREE.CanvasTexture(heightCanvas);
    const heightTextMaterial = new THREE.SpriteMaterial({map: heightTexture});
    const heightTextSprite = new THREE.Sprite(heightTextMaterial);

    // Position the height text on the left side of the wall
    heightTextSprite.position.set(-50, wallHeight / 2, 20);
    heightTextSprite.scale.set(100, 50, 1);

    wall.add(heightTextSprite);

    // Set user data for all elements
    topArrow.userData.id = id;
    bottomArrow.userData.id = id;
    heightTextMaterial.userData.id = id;

    // Add all elements to the arrows array for handling
    arrowsAndText.push(topArrow, bottomArrow, heightTextMaterial);

    // Handle visibility through the callback
    handleArrows(arrowsAndText);

    // Hide by default
    topArrow.visible = false;
    bottomArrow.visible = false;
    heightTextMaterial.visible = false;

    return arrowsAndText;
};

const disableArrowDepthTest = (arrow: THREE.ArrowHelper) => {
    arrow.traverse((child) => {
        if (!(child instanceof THREE.Mesh || child instanceof THREE.Line)) {
            return;
        }
        const material = child.material as THREE.Material | THREE.Material[];
        const materials = Array.isArray(material) ? material : [material];
        materials.forEach((item) => {
            item.depthTest = false;
            item.depthWrite = false;
            item.needsUpdate = true;
        });
    });
};

export const buildHeightArrowsAndTextGroup = (
    wallHeight: number,
    scale = 1,
    id = '',
    defaultLabelFontSize: string,
    position = {
        x: -60,
        z: 65,
    } as {x?: number; z?: number}
) => {
    const group = new THREE.Group();
    group.name = 'heightMeasurementGroup';
    group.userData.type = 'heightMeasurements';
    group.userData.id = id;

    const arrowColor = '#A9A9A9';
    const heightArrowDir = new THREE.Vector3(0, 1, 0).normalize();
    const heightArrowLength = wallHeight;

    const bottomArrow = new THREE.ArrowHelper(
        heightArrowDir.clone().negate(),
        new THREE.Vector3(-20, wallHeight, 0),
        heightArrowLength,
        arrowColor,
        8,
        8
    );
    const topArrow = new THREE.ArrowHelper(
        heightArrowDir,
        new THREE.Vector3(-20, 0, 0),
        heightArrowLength,
        arrowColor,
        8,
        8
    );
    disableArrowDepthTest(bottomArrow);
    disableArrowDepthTest(topArrow);

    const heightCanvas = document.createElement('canvas');
    heightCanvas.width = 156;
    heightCanvas.height = 60;
    const heightContext = heightCanvas.getContext('2d');
    if (heightContext) {
        heightContext.font = defaultLabelFontSize;
        heightContext.fillStyle = '#000';
        heightContext.textAlign = 'center';
        heightContext.textBaseline = 'middle';
        const heightInMm = Math.round(wallHeight / scale);
        heightContext.fillText(
            `${heightInMm}`,
            heightCanvas.width / 2,
            heightCanvas.height / 2
        );
    }

    const heightTexture = new THREE.CanvasTexture(heightCanvas);
    const heightTextMaterial = new THREE.SpriteMaterial({
        map: heightTexture,
        depthTest: false,
        depthWrite: false,
    });
    const heightTextSprite = new THREE.Sprite(heightTextMaterial);
    heightTextSprite.position.set(position.x, wallHeight / 2, position.z);
    heightTextSprite.scale.set(100, 50, 1);

    topArrow.userData.id = id;
    bottomArrow.userData.id = id;
    heightTextMaterial.userData.id = id;

    // Help global show/hide toggles
    topArrow.userData.isVisibilityControlled = true;
    bottomArrow.userData.isVisibilityControlled = true;
    heightTextMaterial.userData.isVisibilityControlled = true;

    group.add(bottomArrow);
    group.add(topArrow);
    group.add(heightTextSprite);

    group.userData.updateVisibility = (show: boolean) => {
        group.visible = show;
    };

    const arrowObjects: (THREE.ArrowHelper | THREE.SpriteMaterial)[] = [
        topArrow,
        bottomArrow,
        heightTextMaterial,
    ];

    return {group, arrowObjects};
};

export const addAllCabinetDimensionsToBackWall = (
    cabinetGroupList: THREE.Group<THREE.Object3DEventMap>[],
    backWall: THREE.Mesh,
    handleArrowsAndGroup: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial)[],
        arrowGroup?: THREE.Group<THREE.Object3DEventMap>
    ) => void,
    getGroupWidth: (group: THREE.Group<THREE.Object3DEventMap>) => {
        width: number;
        height: number;
        depth: number;
    }
): THREE.Group<THREE.Object3DEventMap> | null => {
    if (!cabinetGroupList || !cabinetGroupList.length || !backWall) return null;

    // Create a new group for the dimension arrows
    const dimensionGroup = new THREE.Group();
    dimensionGroup.name = 'cabinetBackWallDimensions';
    dimensionGroup.userData.id = 'cabinetBackWallDimensions';

    // Position the group near the top of the back wall
    const backWallPosition = backWall.position.clone();
    const backWallBoundingBox = new THREE.Box3().setFromObject(backWall);
    const backWallHeight =
        backWallBoundingBox.max.y - backWallBoundingBox.min.y;

    dimensionGroup.position.copy(backWallPosition);
    dimensionGroup.position.y += backWallHeight / 2 + 20; // Position above the back wall

    // Add text displaying cabinet dimensions
    const createDimensionText = (
        text: string,
        position: THREE.Vector3
    ): THREE.Sprite => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return;
        canvas.width = 512; // Wider canvas for more text
        canvas.height = 64;

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        context.font = 'Bold 18px Arial';
        context.fillStyle = '#000000';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({map: texture});
        const sprite = new THREE.Sprite(material);
        sprite.userData.text = text;
        sprite.scale.set(80, 10, 1);
        sprite.position.copy(position);

        return sprite;
    };

    // Function to update dimensions for all cabinets
    dimensionGroup.userData.updateAllDimensions = () => {
        // Clear existing children
        while (dimensionGroup.children.length > 0) {
            dimensionGroup.remove(dimensionGroup.children[0]);
        }

        if (!cabinetGroupList || cabinetGroupList.length === 0) return;

        // Create combined text for all cabinets
        let allCabinetsText = 'Cabinet Dimensions:';

        cabinetGroupList.forEach((cabinet, index) => {
            if (!cabinet) return;

            const cabinetSize = getGroupWidth(cabinet);
            const isManualPanel = cabinet.userData.isManualPanel as boolean;

            // For manual panels, only fix the width - use actual form value
            // Height and depth remain as calculated from bounding box (existing behavior)
            let formattedWidth: string;
            if (isManualPanel) {
                const cabinetValues = cabinet.userData
                    .values as RoomPlannerProductWithTemplate;
                const panelWidth =
                    Number(cabinetValues?.cabinet_panel_width) || 0;
                formattedWidth = panelWidth.toFixed(1);
            } else {
                formattedWidth = cabinetSize.width.toFixed(1);
            }

            // Keep existing behavior for depth and height
            const formattedDepth = cabinetSize.depth.toFixed(1);
            const formattedHeight = cabinetSize.height.toFixed(1);

            // Add cabinet name and dimensions
            const cabinetName = cabinet.name || `Cabinet ${index + 1}`;
            allCabinetsText += ` ${cabinetName}: ${formattedWidth}x${formattedDepth}x${formattedHeight}`;

            // Add separator between cabinets
            if (index < cabinetGroupList.length - 1) {
                allCabinetsText += ' | ';
            }
        });

        // Create the text sprite and add to dimensionGroup
        const textSprite = createDimensionText(
            allCabinetsText,
            new THREE.Vector3(0, 0, 0)
        );
        dimensionGroup.add(textSprite);
    };

    (
        dimensionGroup.userData as {updateAllDimensions: () => void}
    ).updateAllDimensions();
    // Initial update with current dimensions

    // Add the dimension group to the scene
    backWall.parent?.add(dimensionGroup);

    if (handleArrowsAndGroup) {
        handleArrowsAndGroup([], dimensionGroup);
    }

    return dimensionGroup;
};

// Movement Arrow When clicking
export const addCabinetMovementArrows = (
    cabinetGroup: THREE.Group,
    handleArrowsAndGroup: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial | THREE.Mesh)[],
        arrowGroup?: THREE.Group<THREE.Object3DEventMap>
    ) => void
) => {
    // Get cabinet dimensions

    // Create a new group for the movement arrows
    const arrowGroup = new THREE.Group();
    arrowGroup.name = 'cabinetMovementArrows';
    // Set high render order to ensure arrow group is always rendered on top
    arrowGroup.renderOrder = 1000;

    // Add a unique ID to identify this arrow group
    arrowGroup.userData.id =
        (cabinetGroup.userData.id as string) || 'cabinetMovementArrows';

    // Store all arrow objects
    const arrowObjects: THREE.Mesh[] = [];

    // Store measurement objects
    const measurementObjects: THREE.Object3D[] = [];

    // Method to show/hide arrows based on movement mode state
    // isActive represents the new isMovementMode state (same as setDistanceMeasurementsActive)
    arrowGroup.userData.updateVisibility = (isActive: boolean) => {
        // Show arrows when movement mode is active
        // Visibility during dragging is handled by dragVisibilityHelpers.ts
        const shouldShow = isActive;
        arrowGroup.visible = shouldShow;
        setVisibility(arrowObjects, shouldShow);
        setVisibility(measurementObjects, true);
    };

    arrowGroup.userData.updateVisibilityDirection = (isActive: boolean) => {
        setVisibility(measurementObjects, isActive);
    };

    // Initial state - hidden
    arrowGroup.visible = false;

    // Create movement arrows

    const createMovementArrows = () => {
        // Clear existing arrows
        while (arrowGroup.children.length > 0) {
            arrowGroup.remove(arrowGroup.children[0]);
        }
        arrowObjects.length = 0;
        measurementObjects.length = 0;

        // Create 6 directional flat arrows for movement
        const createFlatArrow = (
            direction: THREE.Vector3,
            position: THREE.Vector3
        ) => {
            // Create a custom shape for the arrow
            const arrowShape = new THREE.Shape();

            // Define arrow shape points for a more prominent arrow
            arrowShape.moveTo(0, -40); // Top point (enlarged)
            arrowShape.lineTo(30, 5); // Bottom right (enlarged)
            arrowShape.lineTo(15, 5); // Start of arrow shaft
            arrowShape.lineTo(15, 20); // Arrow shaft
            arrowShape.lineTo(-15, 20); // Arrow shaft
            arrowShape.lineTo(-15, 5); // End of arrow shaft
            arrowShape.lineTo(-30, 5); // Bottom left (enlarged)
            arrowShape.lineTo(0, -40); // Back to top

            // Extrude settings - thicker arrow
            const extrudeSettings = {
                depth: 7, // Increased thickness
                bevelEnabled: true,
                bevelThickness: 3, // Increased bevel
                bevelSize: 2, // Increased bevel size
                bevelSegments: 4, // Smoother bevel
            };

            // Create extruded geometry
            const arrowGeometry = new THREE.ExtrudeGeometry(
                arrowShape,
                extrudeSettings
            );

            // Create material - brighter and more vibrant
            const arrowMaterial = new THREE.MeshPhongMaterial({
                color: 0x29b6f6,
                shininess: 120, // Increased shininess
                transparent: true,
                opacity: 0.95, // Slightly increased opacity
                emissive: 0x0a4a7d, // Add a slight glow
                emissiveIntensity: 0.3,
                depthTest: false, // Disable depth testing so arrow is always visible
                depthWrite: false, // Don't write to depth buffer
            });

            // Create the arrow mesh
            const arrowMesh = new THREE.Mesh(arrowGeometry, arrowMaterial);
            arrowMesh.position.copy(position);
            // Set high render order to ensure arrow is always rendered on top
            arrowMesh.renderOrder = 1000;
            arrowMesh.frustumCulled = false;

            // Set the rotation based on the direction to make arrows point outward from surfaces
            if (direction.x < 0) {
                // Left arrow - pointing left (negative X)
                arrowMesh.rotation.z = -Math.PI / 2;
            } else if (direction.x > 0) {
                // Right arrow - pointing right (positive X)
                arrowMesh.rotation.z = Math.PI / 2;
            } else if (direction.y < 0) {
                // Front arrow - pointing front (negative Y)
                arrowMesh.rotation.z = 0;
            } else if (direction.y > 0) {
                // Back arrow - pointing back (positive Y)
                arrowMesh.rotation.z = Math.PI;
            } else if (direction.z < 0) {
                // Down arrow - pointing down (negative Z)
                arrowMesh.rotation.x = Math.PI / 2;
            } else if (direction.z > 0) {
                // Up arrow - pointing up (positive Z)
                arrowMesh.rotation.x = -Math.PI / 2;
            }

            // Center the geometry
            arrowMesh.geometry.center();

            arrowGroup.add(arrowMesh);
            arrowObjects.push(arrowMesh);
            return arrowMesh;
        };

        // Get the current direction of the cabinet
        const currentDirection =
            (cabinetGroup.userData.currentDirection as string) || 'front';
        const {width, height, depth} = getGroupWidth(cabinetGroup);

        // Determine effective dimensions based on rotation
        const effectiveWidth =
            currentDirection === 'left' || currentDirection === 'right'
                ? depth
                : width;
        const effectiveDepth =
            currentDirection === 'left' || currentDirection === 'right'
                ? width
                : depth;

        // Direction vectors and positions with adjusted dimensions
        const directions = [
            {
                dir: new THREE.Vector3(-1, 0, 0),
                position: new THREE.Vector3(
                    -40,
                    effectiveDepth / 2,
                    height - 20
                ),
                direction: 'left',
            },
            {
                dir: new THREE.Vector3(1, 0, 0),
                position: new THREE.Vector3(
                    effectiveWidth + 40,
                    effectiveDepth / 2,
                    height - 20
                ),
                direction: 'right',
            },
            {
                dir: new THREE.Vector3(0, 0, 1),
                position: new THREE.Vector3(
                    effectiveWidth / 2,
                    effectiveDepth / 2,
                    height + 120
                ),
                direction: 'up',
            },
            {
                dir: new THREE.Vector3(0, 0, -1),
                position: new THREE.Vector3(
                    effectiveWidth / 2,
                    effectiveDepth / 2,
                    height + 30
                ),
                direction: 'down',
            },
            {
                dir: new THREE.Vector3(0, 1, 0),
                position: new THREE.Vector3(
                    effectiveWidth / 2,
                    effectiveDepth + 25,
                    height - 20
                ),
                direction: 'back',
            },
            {
                dir: new THREE.Vector3(0, -1, 0),
                position: new THREE.Vector3(
                    effectiveWidth / 2,
                    -25,
                    height - 20
                ),
                direction: 'front',
            },
        ];

        // Create arrows for each direction
        directions.forEach(({dir, position, direction}) => {
            const arrow = createFlatArrow(dir, position);
            // Store direction data in arrow's userData
            arrow.userData.direction = direction;
            arrow.userData.worldPosition = position.clone(); // Store world position for input field placement
        });
    };

    // Method to update arrow positions when cabinet changes
    arrowGroup.userData.updateArrows = () => {
        // Reset hover states on all arrows before recreating
        arrowObjects.forEach((arrow) => {
            if (arrow.userData && arrow.userData.isHovered) {
                if (typeof arrow.userData.onHoverEnd === 'function') {
                    (
                        arrow.userData as {
                            onHoverEnd: () => void;
                        }
                    ).onHoverEnd();
                }
            }
        });

        // Update dimensions (recreates arrows)
        createMovementArrows();

        // Update position and rotation to match cabinet
        syncOverlayTransformToCabinet(arrowGroup, cabinetGroup);

        // Setup interactions again to ensure all user data is properly initialized
        (
            arrowGroup.userData as {
                setupInteractions: () => void;
            }
        ).setupInteractions();

        // Re-register with arrow handler
        handleArrowsAndGroup(arrowObjects, arrowGroup);
    };

    // Visibility is managed externally by dragVisibilityHelpers.ts.
    // onDrag only rebuilds geometry and syncs position/rotation.
    arrowGroup.userData.onDrag = (
        width: number,
        depth: number,
        height: number
    ) => {
        // Check if we need to update dimensions based on orientation
        const isHorizontal =
            cabinetGroup.userData.currentDirection === 'left' ||
            cabinetGroup.userData.currentDirection === 'right';

        if (!isHorizontal) {
            (
                arrowGroup.userData as {
                    updateArrows: (
                        width: number,
                        depth: number,
                        height: number
                    ) => void;
                }
            ).updateArrows(width, depth, height);
        } else {
            (
                arrowGroup.userData as {
                    updateArrows: (
                        width: number,
                        depth: number,
                        height: number
                    ) => void;
                }
            ).updateArrows(depth, width, height);
        }

        // Ensure arrow group stays with cabinet
        syncOverlayTransformToCabinet(arrowGroup, cabinetGroup);
    };

    arrowGroup.userData.onDragEnd = () => {
        arrowGroup.visible = false;
    };

    // Setup click interactions with the arrows
    arrowGroup.userData.setupInteractions = () => {
        arrowObjects.forEach((arrow, index) => {
            // Make the arrow interactive
            arrow.userData.clickable = true;
            arrow.userData.isArrow = true;
            arrow.userData.arrowIndex = index;
            arrow.userData.isHovered = false; // Initialize hover state

            // Add a hover effect
            arrow.userData.onHover = () => {
                // Scale the arrow up more noticeably when hovered
                arrow.scale.set(2.0, 2.0, 2.0);

                // Make the arrow brighter and more vibrant when hovered
                if (arrow.material instanceof THREE.MeshPhongMaterial) {
                    // Store original color if not already stored
                    if (!arrow.userData.originalColor) {
                        arrow.userData.originalColor =
                            arrow.material.color.getHex();
                        arrow.userData.originalEmissive =
                            arrow.material.emissive.getHex();
                        arrow.userData.originalOpacity = arrow.material.opacity;
                        arrow.userData.originalEmissiveIntensity =
                            arrow.material.emissiveIntensity;
                    }

                    // Brighten the color - more dramatic color change for better visibility
                    arrow.material.color.setHex(0x00e5ff); // Bright cyan blue
                    arrow.material.emissive.setHex(0x00a0b0); // Matching emissive glow
                    arrow.material.opacity = 1.0; // Full opacity
                    arrow.material.emissiveIntensity = 0.7; // Stronger glow
                }

                // Flag the arrow as currently being hovered
                arrow.userData.isHovered = true;
            };

            arrow.userData.onHoverEnd = () => {
                // Return to original scale
                arrow.scale.set(1, 1, 1);

                // Restore original colors
                if (
                    arrow.material instanceof THREE.MeshPhongMaterial &&
                    arrow.userData.originalColor !== undefined
                ) {
                    arrow.material.color.setHex(
                        arrow.userData.originalColor as number
                    );
                    arrow.material.emissive.setHex(
                        arrow.userData.originalEmissive as number
                    );
                    arrow.material.opacity = arrow.userData
                        .originalOpacity as number;
                    arrow.material.emissiveIntensity = arrow.userData
                        .originalEmissiveIntensity as number;
                }

                // Clear the hover flag
                arrow.userData.isHovered = false;
            };

            // Add click handler for each arrow
            arrow.userData.onClick = () => {
                // Get the clicked arrow's direction and world position
                const direction = arrow.userData.direction as string;
                const worldPosition = (
                    arrow.userData.worldPosition as THREE.Vector3
                ).clone();

                // Add the cabinet's position to get the actual world position
                worldPosition.add(cabinetGroup.position);

                // Convert world position to screen coordinates for input field placement
                const vector = worldPosition.clone();
                vector.project(cabinetGroup.userData.camera as THREE.Camera);

                const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
                const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;

                // Hide all other arrows except this one
                for (const otherArrow of arrowObjects) {
                    if (otherArrow !== arrow) {
                        otherArrow.visible = false;
                    }
                }

                // Show all measurement lines when an arrow is clicked
                setVisibility(measurementObjects, true);

                // Trigger position editor display
                if (cabinetGroup.userData.onArrowClick) {
                    (
                        cabinetGroup.userData as {
                            onArrowClick: (params: {
                                direction: string;
                                screenX: number;
                                screenY: number;
                                arrowMesh: THREE.Mesh;
                            }) => void;
                        }
                    ).onArrowClick({
                        direction,
                        screenX: x,
                        screenY: y,
                        arrowMesh: arrow,
                    });
                }
            };
        });
    };

    // Add click handler to the arrow group
    arrowGroup.userData.handleClick = (
        event: MouseEvent,
        raycaster: THREE.Raycaster,
        camera: THREE.Camera
    ) => {
        // Store camera reference for coordinate conversion
        cabinetGroup.userData.camera = camera;

        // Only proceed if arrow group is visible
        if (!arrowGroup.visible) {
            return;
        }

        // Check which arrow was clicked - only consider visible arrows
        const visibleArrows = arrowObjects.filter((arrow) => arrow.visible);
        const intersects = raycaster.intersectObjects(visibleArrows, true);

        if (intersects.length > 0) {
            const clickedArrow = intersects[0].object;

            // Find the parent arrow mesh (in case we clicked a child object)
            const arrowMesh = clickedArrow.parent?.userData.isArrow
                ? clickedArrow.parent
                : clickedArrow;

            setVisibility(measurementObjects, true);

            // Only proceed if both the arrow and arrow group are visible
            if (arrowMesh && arrowMesh.visible && arrowGroup.visible) {
                // Hide all other arrows
                for (const otherArrow of arrowObjects) {
                    if (otherArrow !== arrowMesh) {
                        otherArrow.visible = false;
                    }
                }

                // Show all measurement lines when an arrow is clicked
                setVisibility(measurementObjects, true);

                // Get the direction and world position from the arrow's userData
                const direction = arrowMesh.userData.direction as string;
                const worldPosition = arrowMesh.getWorldPosition(
                    new THREE.Vector3()
                );

                // Convert world position to screen coordinates for input field placement
                const vector = worldPosition.clone();
                vector.project(camera);

                const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
                const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;

                // Trigger position editor display
                if (cabinetGroup.userData.onArrowClick) {
                    (
                        cabinetGroup.userData as {
                            onArrowClick: (params: {
                                direction: string;
                                screenX: number;
                                screenY: number;
                                arrowMesh: THREE.Mesh;
                            }) => void;
                        }
                    ).onArrowClick({
                        direction,
                        screenX: x,
                        screenY: y,
                        arrowMesh: arrowMesh as THREE.Mesh,
                    });
                }
            }
        }
    };

    // Store references to arrows and their handlers in the cabinet group
    cabinetGroup.userData.movementArrows = arrowObjects;
    cabinetGroup.userData.arrowGroup = arrowGroup;

    // Add method to show/hide arrows
    cabinetGroup.userData.showMovementArrows = (show: boolean) => {
        if (arrowObjects.length > 0) {
            setVisibility(arrowObjects, show);
        }
    };

    // Add method to reset arrow visibility
    cabinetGroup.userData.resetArrowVisibility = () => {
        if (arrowObjects.length > 0) {
            setVisibility(arrowObjects, true);
        }
    };

    handleArrowsAndGroup(arrowObjects, arrowGroup);

    // Add method to reset arrow visibility
    arrowGroup.userData.resetArrows = () => {
        setVisibility(arrowObjects, true);
    };

    // Add dispose method
    arrowGroup.userData.dispose = () => {
        // Properly dispose of materials and textures
        for (const arrow of arrowObjects) {
            if (arrow instanceof THREE.Mesh) {
                if (
                    arrow.material instanceof THREE.MeshBasicMaterial &&
                    arrow.material.map
                ) {
                    arrow.material.map.dispose();
                    arrow.material.dispose();
                }
                if (arrow.geometry) {
                    arrow.geometry.dispose();
                }
            }
        }

        if (arrowGroup.parent) {
            arrowGroup.parent.remove(arrowGroup);
        }
    };

    // Add the dispose method to the cabinet group
    cabinetGroup.userData.disposeMovementArrows = () => {
        (
            arrowGroup.userData as {
                dispose: () => void;
            }
        ).dispose();
    };

    // Create initial arrows
    createMovementArrows();

    // Debug lines removed as requested
    // createDebugLines()

    // Setup interactions
    (
        arrowGroup.userData as {
            setupInteractions: () => void;
        }
    ).setupInteractions();

    // Add arrow group to the scene at the cabinet's position
    if (cabinetGroup.parent) {
        cabinetGroup.parent.add(arrowGroup);
    } else {
        return null;
    }

    // Position arrow group to match cabinet position
    syncOverlayTransformToCabinet(arrowGroup, cabinetGroup);

    // Set initial visibility to false
    arrowGroup.visible = false;

    // Register with arrow handler
    handleArrowsAndGroup(arrowObjects, arrowGroup);

    // Listen for active group changes
    cabinetGroup.userData.setActiveState = (isActive: boolean) => {
        cabinetGroup.userData.isActiveGroup = isActive;
        (
            arrowGroup.userData as {
                updateVisibility: (isActive: boolean) => void;
            }
        ).updateVisibility(isActive);
    };

    return arrowGroup;
};

const DEFAULT_LABEL_FONT_SIZE = '40px Arial';

export const addDistanceWallMeasurements = (
    cabinetGroup: THREE.Group,
    handleArrowsAndGroup: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial | THREE.Mesh)[],
        arrowGroup?: THREE.Group<THREE.Object3DEventMap>
    ) => void,
    roomType: string,
    rectangularDimension: {
        width: number;
        length: number;
        height?: number;
    },
    lShapedDimension: {
        rightWidth: number;
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
    },
    scale: number,
    cabinetGroupList?: THREE.Group<THREE.Object3DEventMap>[]
) => {
    const measurementConfig: MeasurementConfig = {
        wallHeight:
            rectangularDimension.height != null
                ? rectangularDimension.height * scale + 20
                : 0,
        defaultLabelFontSize: DEFAULT_LABEL_FONT_SIZE,
        roomWidth: rectangularDimension.width,
        roomLength: rectangularDimension.length,
        leftWidth: lShapedDimension.leftWidth,
        rightWidth: lShapedDimension.rightWidth,
        leftDepth: lShapedDimension.leftDepth,
        rightDepth: lShapedDimension.rightDepth,
        scale,
        showArrows: true,
    };
    // Arrow offset configuration
    const ARROW_OFFSETS = {
        primary: 35, // Main arrow offset
        edge: 12.5, // Unified offset for front/left/right walls
        back: 12.9, // Back wall offset
        backAlt: 12.1, // Alternative back wall offset
        frontAlt: 34.6, // Alternative front wall offset
        rightAlt: 38.6, // Alternative right wall offset
        position: 5, // Position offset for start points
        // General small adjustments (formerly magic numbers)
        epsilon: 0.1,
        small: 0.4,
        lShapedExtra: 2.7,
        depthAdjustment: 15,
        rightAltExtra: 4.1,
        noDoorThickness: -0.6,
    };

    // Label rendering configuration: a pill badge centred on the measurement
    // line. The canvas is drawn at 2x the on-screen size so it stays crisp;
    // its width follows the number.
    const LABEL_CONFIG = {
        canvas: {
            height: DISTANCE_LABEL_STYLE.canvasHeight,
        },
        pill: {
            paddingX: DISTANCE_LABEL_STYLE.paddingX,
            radius: DISTANCE_LABEL_STYLE.radius,
            borderWidth: DISTANCE_LABEL_STYLE.borderWidth,
            fill: DISTANCE_LABEL_STYLE.fill,
            border: DISTANCE_LABEL_STYLE.border,
        },
        font: {
            style: `${DISTANCE_LABEL_STYLE.fontWeight} ${DISTANCE_LABEL_STYLE.fontSize}px ${DISTANCE_LABEL_STYLE.fontFamily}`,
            color: DISTANCE_LABEL_STYLE.text,
            align: 'center' as CanvasTextAlign,
            baseline: 'middle' as CanvasTextBaseline,
        },
        sprite: {
            scale: {
                // Width is derived from the canvas aspect ratio.
                x: 0,
                y: DISTANCE_LABEL_STYLE.spriteHeight,
                z: 1,
            },
            material: {
                transparent: true,
                depthTest: false,
                depthWrite: false,
                alphaTest: 0.01,
                sizeAttenuation: false,
            },
        },
    };

    // Arrow rendering configuration
    const ARROW_CONFIG = {
        color: 0x29b6f6,
        headLength: 8,
        headWidth: 8,
        renderOrder: 5,
    };

    // Get cabinet dimensions
    // Create a new group for the movement arrows
    const arrowGroup = new THREE.Group();
    arrowGroup.name = 'distanceWallMeasurements';
    arrowGroup.renderOrder = ARROW_CONFIG.renderOrder; // Very high render order to be on top
    // Add a unique ID to identify this arrow group
    arrowGroup.userData.id =
        (cabinetGroup.userData.id as string) || 'distanceWallMeasurements';

    // Store all arrow objects
    const arrowObjects: THREE.Mesh[] = [];

    // Store measurement objects
    const measurementObjects: THREE.Object3D[] = [];

    // Method to show/hide arrows based on active state
    arrowGroup.userData.updateVisibility = (isActive: boolean) => {
        arrowGroup.visible = isActive;
        setVisibility(arrowObjects, isActive);
        setVisibility(measurementObjects, isActive);
    };

    arrowGroup.userData.updateVisibilityDirection = (isActive: boolean) => {
        setVisibility(measurementObjects, isActive);
    };

    // Initial state - hidden (same as addCabinetMovementArrows)
    arrowGroup.visible = false;
    arrowGroup.renderOrder = ARROW_CONFIG.renderOrder; // Set high render order
    // Create measurement line from cabinet to wall - uses the shared distance calculation
    const createWallDistanceMeasurement = (
        direction: string,
        withDecimal = false
    ) => {
        // Create a group for the measurement
        const measurementGroup = new THREE.Group();
        measurementGroup.name = 'wallDistanceMeasurement';
        measurementGroup.renderOrder = ARROW_CONFIG.renderOrder;

        const {width, depth} = getGroupWidth(cabinetGroup);

        const arrowColor = ARROW_CONFIG.color;
        const lineY = depth / 2;
        const arrowHeadLength = ARROW_CONFIG.headLength;
        const arrowHeadWidth = ARROW_CONFIG.headWidth;
        const basePositionY = lineY;
        const positionZ = 0;

        const currentDirection =
            (cabinetGroup.userData.currentDirection as string) || 'front';

        // Direction vectors for arrows
        const leftToRightDir = new THREE.Vector3(1, 0, 0).normalize();
        const rightToLeftDir = new THREE.Vector3(-1, 0, 0).normalize();
        const frontToBackDir = new THREE.Vector3(0, 1, 0).normalize();
        const backToFrontDir = new THREE.Vector3(0, -1, 0).normalize();

        // Use the shared wall distance calculation
        const wallRoomConfig: RoomConfig = {
            roomType,
            rectangularDimension: {
                width: rectangularDimension.width,
                length: rectangularDimension.length,
                height: 0,
            },
            lShapedDimension: {
                ...lShapedDimension,
                height: 0,
            },
            scale,
        };
        const distances = calculateCabinetWallDistances(
            cabinetGroup,
            wallRoomConfig
        );

        // Get arrow length from the shared calculation
        let actualArrowLength: number;
        switch (direction) {
            case 'left':
                actualArrowLength = Math.abs(distances.left);
                break;
            case 'right':
                actualArrowLength = Math.abs(distances.right);
                break;
            case 'front':
                actualArrowLength = Math.abs(distances.front);
                break;
            case 'back':
                actualArrowLength = Math.abs(distances.back);
                break;
            default:
                actualArrowLength = 0;
                break;
        }

        // Determine start point and arrow direction based on cabinet orientation
        let startPoint: THREE.Vector3;
        let arrowDir: THREE.Vector3;

        if (currentDirection === 'front') {
            switch (direction) {
                case 'left':
                    startPoint = new THREE.Vector3(0, basePositionY, positionZ);
                    arrowDir = rightToLeftDir;
                    break;
                case 'right':
                    startPoint = new THREE.Vector3(
                        width,
                        basePositionY,
                        positionZ
                    );
                    arrowDir = leftToRightDir;
                    break;
                case 'front':
                    startPoint = new THREE.Vector3(
                        width / 2,
                        -ARROW_OFFSETS.position,
                        positionZ
                    );
                    arrowDir = backToFrontDir;
                    break;
                case 'back':
                default:
                    startPoint = new THREE.Vector3(
                        width / 2,
                        depth - ARROW_OFFSETS.position,
                        positionZ
                    );
                    arrowDir = frontToBackDir;
                    break;
            }
        } else if (currentDirection === 'left') {
            switch (direction) {
                case 'left':
                    startPoint = new THREE.Vector3(0, width / 2, positionZ);
                    arrowDir = rightToLeftDir;
                    break;
                case 'right':
                    startPoint = new THREE.Vector3(depth, width / 2, positionZ);
                    arrowDir = leftToRightDir;
                    break;
                case 'front':
                    startPoint = new THREE.Vector3(depth / 2, 0, positionZ);
                    arrowDir = backToFrontDir;
                    break;
                case 'back':
                default:
                    startPoint = new THREE.Vector3(depth / 2, width, positionZ);
                    arrowDir = frontToBackDir;
                    break;
            }
        } else if (currentDirection === 'right') {
            switch (direction) {
                case 'left':
                    startPoint = new THREE.Vector3(0, width / 2, positionZ);
                    arrowDir = rightToLeftDir;
                    break;
                case 'right':
                    startPoint = new THREE.Vector3(depth, width / 2, positionZ);
                    arrowDir = leftToRightDir;
                    break;
                case 'front':
                    startPoint = new THREE.Vector3(depth / 2, 0, positionZ);
                    arrowDir = backToFrontDir;
                    break;
                case 'back':
                default:
                    startPoint = new THREE.Vector3(depth / 2, width, positionZ);
                    arrowDir = frontToBackDir;
                    break;
            }
        } else {
            // currentDirection === 'back'
            switch (direction) {
                case 'left':
                    startPoint = new THREE.Vector3(
                        width,
                        basePositionY,
                        positionZ
                    );
                    arrowDir = leftToRightDir;
                    break;
                case 'right':
                    startPoint = new THREE.Vector3(0, basePositionY, positionZ);
                    arrowDir = rightToLeftDir;
                    break;
                case 'front':
                    startPoint = new THREE.Vector3(width / 2, depth, positionZ);
                    arrowDir = frontToBackDir;
                    break;
                case 'back':
                default:
                    startPoint = new THREE.Vector3(width / 2, 0, positionZ);
                    arrowDir = backToFrontDir;
                    break;
            }
        }

        // Ensure positive arrow length
        if (actualArrowLength < 0)
            actualArrowLength = Math.abs(actualArrowLength);

        const trueGap = getTrueWallGapForArrow(
            cabinetGroup,
            currentDirection,
            direction
        );
        if (trueGap !== null && trueGap / scale <= WALL_FLUSH_TOLERANCE_MM) {
            actualArrowLength = 0;
        }

        // Create the arrow
        if (actualArrowLength > 0) {
            const arrow = new THREE.ArrowHelper(
                arrowDir,
                startPoint,
                actualArrowLength,
                arrowColor,
                arrowHeadLength,
                arrowHeadWidth
            );
            measurementGroup.add(arrow);
        }

        // Create text label showing the distance (round mm to 1 decimal to avoid .99999999)
        const mmValue = actualArrowLength / scale;

        const displayMm = !withDecimal
            ? Math.round(mmValue)
            : Number(Math.round(mmValue * 10) / 10);
        const textSprite = createMeasurementTextSprite(
            displayMm,
            direction,
            arrowGroup,
            cabinetGroup,
            LABEL_CONFIG
        );

        if (textSprite) {
            // Position sprite based on direction and cabinet orientation
            const spritePosition = getTextSpritePosition(
                direction,
                currentDirection,
                actualArrowLength,
                width,
                depth
            );
            textSprite.position.copy(spritePosition);
            measurementGroup.add(textSprite);
        }

        arrowGroup.add(measurementGroup);
        measurementObjects.push(measurementGroup);

        return measurementGroup;
    };

    // Method to update arrow positions when cabinet changes
    arrowGroup.userData.updateArrows = () => {
        // Reset hover states on all arrows before recreating
        arrowObjects.forEach((arrow) => {
            if (arrow.userData && arrow.userData.isHovered) {
                if (typeof arrow.userData.onHoverEnd === 'function') {
                    (
                        arrow.userData as {
                            onHoverEnd: () => void;
                        }
                    ).onHoverEnd();
                }
            }
        });

        // Update dimensions (recreates arrows)
        // createMovementArrows();

        // Update position and rotation to match cabinet
        syncOverlayTransformToCabinet(arrowGroup, cabinetGroup);

        // Setup interactions again to ensure all user data is properly initialized
        (
            arrowGroup.userData as {
                setupInteractions: () => void;
            }
        ).setupInteractions();

        // Re-register with arrow handler
        handleArrowsAndGroup(arrowObjects, arrowGroup);

        // Log hover state transfer operation
    };

    // Methods for showing/hiding arrows
    arrowGroup.userData.onDrag = (
        width: number,
        depth: number,
        height: number
    ) => {
        // Visibility is handled by dragVisibilityHelpers.ts
        // This handler only updates positions and recreates measurements
        // It's called for both dragging and arrow key movements

        // Check if we need to update dimensions based on orientation
        const isHorizontal =
            cabinetGroup.userData.currentDirection === 'left' ||
            cabinetGroup.userData.currentDirection === 'right';

        if (!isHorizontal) {
            (
                arrowGroup.userData as {
                    updateArrows: (
                        width: number,
                        depth: number,
                        height: number
                    ) => void;
                }
            ).updateArrows(width, depth, height);
        } else {
            (
                arrowGroup.userData as {
                    updateArrows: (
                        width: number,
                        depth: number,
                        height: number
                    ) => void;
                }
            ).updateArrows(depth, width, height);
        }

        // Ensure arrow group stays with cabinet
        syncOverlayTransformToCabinet(arrowGroup, cabinetGroup);

        // Clear existing measurements and recreate them with updated positions
        // This ensures they reflect the new cabinet position
        measurementObjects.forEach((obj) => {
            if (obj.name === 'wallDistanceMeasurement') {
                arrowGroup.remove(obj);
            }
        });

        // Filter out removed measurement objects
        const remainingObjects = measurementObjects.filter(
            (obj) => obj.name !== 'wallDistanceMeasurement'
        );
        measurementObjects.length = 0;
        measurementObjects.push(...remainingObjects);

        const hasDecimalByOrientation = cabinetGroupList?.length
            ? getHasDecimalInMeasurementLabelsByOrientation(
                  cabinetGroupList,
                  measurementConfig
              )
            : null;
        const vertical = hasDecimalByOrientation?.vertical ?? false;
        const horizontal = hasDecimalByOrientation?.horizontal ?? false;
        if (cabinetGroup.userData.currentDirection === 'right') {
            createWallDistanceMeasurement('left', vertical);
            createWallDistanceMeasurement('right', vertical);
            createWallDistanceMeasurement('front', horizontal);
            createWallDistanceMeasurement('back', horizontal);
        } else if (cabinetGroup.userData.currentDirection === 'left') {
            createWallDistanceMeasurement('left', horizontal);
            createWallDistanceMeasurement('right', horizontal);
            createWallDistanceMeasurement('front', vertical);
            createWallDistanceMeasurement('back', vertical);
        } else {
            createWallDistanceMeasurement('left', horizontal);
            createWallDistanceMeasurement('right', horizontal);
            createWallDistanceMeasurement('front', vertical);
            createWallDistanceMeasurement('back', vertical);
        }
    };

    arrowGroup.userData.onDragEnd = () => {
        // Show arrows and measurements again after drag ends if movement mode is still active
        const isMovementMode = cabinetGroup.userData.isMovementMode as boolean;
        const isActiveGroup = cabinetGroup.userData.isActiveGroup as boolean;

        if (isActiveGroup && isMovementMode) {
            arrowGroup.visible = true;
            setVisibility(arrowObjects, true);
            setVisibility(measurementObjects, true);
        } else {
            arrowGroup.visible = false;
            setVisibility(measurementObjects, false);
        }
    };

    // Setup click interactions with the arrows
    arrowGroup.userData.setupInteractions = () => {
        arrowObjects.forEach((arrow, index) => {
            // Make the arrow interactive
            arrow.userData.clickable = true;
            arrow.userData.isArrow = true;
            arrow.userData.arrowIndex = index;
            arrow.userData.isHovered = false; // Initialize hover state

            // Add a hover effect
            arrow.userData.onHover = () => {
                // Scale the arrow up more noticeably when hovered
                arrow.scale.set(2.0, 2.0, 2.0);

                // Flag the arrow as currently being hovered
                arrow.userData.isHovered = true;
            };

            arrow.userData.onHoverEnd = () => {
                // Return to original scale
                arrow.scale.set(1, 1, 1);

                // Restore original colors
                if (
                    arrow.material instanceof THREE.MeshPhongMaterial &&
                    arrow.userData.originalColor !== undefined
                ) {
                    arrow.material.color.setHex(
                        arrow.userData.originalColor as number
                    );
                    arrow.material.emissive.setHex(
                        arrow.userData.originalEmissive as number
                    );
                    arrow.material.opacity = arrow.userData
                        .originalOpacity as number;
                    arrow.material.emissiveIntensity = arrow.userData
                        .originalEmissiveIntensity as number;
                }

                // Clear the hover flag
                arrow.userData.isHovered = false;
            };

            // Add click handler for each arrow
            arrow.userData.onClick = () => {
                // Get the clicked arrow's direction and world position
                const direction = arrow.userData.direction as string;
                const worldPosition = (
                    arrow.userData.worldPosition as THREE.Vector3
                ).clone();

                // Add the cabinet's position to get the actual world position
                worldPosition.add(cabinetGroup.position);

                // Convert world position to screen coordinates for input field placement
                const vector = worldPosition.clone();
                vector.project(cabinetGroup.userData.camera as THREE.Camera);

                const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
                const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;

                // Hide all other arrows except this one
                arrowObjects.forEach((otherArrow) => {
                    if (otherArrow !== arrow) {
                        otherArrow.visible = false;
                    }
                });

                // Hide all measurement lines when an arrow is clicked
                measurementObjects.forEach((obj) => {
                    obj.visible = true;
                });

                // Trigger position editor display
                if (cabinetGroup.userData.onArrowClick) {
                    (
                        cabinetGroup.userData as {
                            onArrowClick: (params: {
                                direction: string;
                                screenX: number;
                                screenY: number;
                                arrowMesh: THREE.Mesh;
                            }) => void;
                        }
                    ).onArrowClick({
                        direction,
                        screenX: x,
                        screenY: y,
                        arrowMesh: arrow,
                    });
                }
            };
        });
    };

    // Store references to arrows and their handlers in the cabinet group
    cabinetGroup.userData.movementArrows = arrowObjects;
    cabinetGroup.userData.arrowGroup = arrowGroup;

    // Add method to show/hide arrows
    cabinetGroup.userData.showMovementArrows = (show: boolean) => {
        if (arrowObjects.length > 0) {
            setVisibility(arrowObjects, show);
        }
    };

    cabinetGroup.userData.showWallDistanceMeasurements = (show: boolean) => {
        if (measurementObjects.length > 0) {
            setVisibility(measurementObjects, show);
        }
    };

    handleArrowsAndGroup(arrowObjects, arrowGroup);

    // Add method to reset arrow visibility
    arrowGroup.userData.resetArrows = () => {
        setVisibility(arrowObjects, true);
        setVisibility(measurementObjects, true);
    };

    // Add dispose method
    arrowGroup.userData.dispose = () => {
        // Properly dispose of materials and textures
        for (const arrow of arrowObjects) {
            if (arrow instanceof THREE.Mesh) {
                if (
                    arrow.material instanceof THREE.MeshBasicMaterial &&
                    arrow.material.map
                ) {
                    arrow.material.map.dispose();
                    arrow.material.dispose();
                }
                if (arrow.geometry) {
                    arrow.geometry.dispose();
                }
            }
        }

        // Dispose of measurement objects
        for (const obj of measurementObjects) {
            if (obj instanceof THREE.Group) {
                for (const child of obj.children) {
                    if (child instanceof THREE.Line) {
                        (child.geometry as THREE.BufferGeometry).dispose();
                        (child.material as THREE.Material).dispose();
                    }
                    if (child instanceof THREE.Sprite) {
                        if (
                            child.material instanceof THREE.SpriteMaterial &&
                            child.material.map
                        ) {
                            child.material.map.dispose();
                        }
                        child.material.dispose();
                    }
                }
            }
        }

        if (arrowGroup.parent) {
            arrowGroup.parent.remove(arrowGroup);
        }
    };

    // Add the dispose method to the cabinet group
    cabinetGroup.userData.disposeWallDistanceArrows = () => {
        (
            arrowGroup.userData as {
                dispose: () => void;
            }
        ).dispose();
    };

    // Add arrow group to the scene at the cabinet's position
    if (cabinetGroup.parent) {
        cabinetGroup.parent.add(arrowGroup);
    } else {
        return null;
    }

    // Position arrow group to match cabinet position
    syncOverlayTransformToCabinet(arrowGroup, cabinetGroup);

    // Set initial visibility to false (same as addCabinetMovementArrows)
    arrowGroup.visible = true;

    // Register with arrow handler
    handleArrowsAndGroup(arrowObjects, arrowGroup);

    // Listen for movement mode changes and update visibility accordingly
    // Use the same pattern as setActiveState for movement arrows
    cabinetGroup.userData.setDistanceMeasurementsActive = (
        isActive: boolean
    ) => {
        // isActive represents the new isMovementMode state
        // Show distance measurements when movement mode is active
        // Use updateVisibility method (same pattern as movement arrows)
        if (arrowGroup.userData?.updateVisibility) {
            (
                arrowGroup.userData as {
                    updateVisibility: (isActive: boolean) => void;
                }
            ).updateVisibility(isActive);
        } else {
            // Fallback: directly set visibility if updateVisibility doesn't exist
            arrowGroup.visible = isActive;
            setVisibility(arrowObjects, isActive);
            setVisibility(measurementObjects, isActive);
        }
    };

    return arrowGroup;
};

/** Default wall height (mm) when dimension height is not provided. */
const DEFAULT_WALL_HEIGHT_MM = 2800;

/**
 * Floor Y in world space. Matches useRoomLayout createRectangularFloor / createLShapedFloor.
 * @param {string} roomType - 'RECTANGULAR' or 'LSHAPED'
 * @param {number} scale - Room scale factor
 * @param {number} rectangularHeight - Room wall height in mm (rectangular)
 * @param {number} lShapedHeight - Room wall height in mm (L-shaped)
 * @return {number} Floor Y position for dashed lines
 */
const getFloorY = (
    roomType: string,
    scale: number,
    rectangularHeight: number,
    lShapedHeight: number
): number => {
    const rectH = rectangularHeight || DEFAULT_WALL_HEIGHT_MM;
    const lShapeH = lShapedHeight || DEFAULT_WALL_HEIGHT_MM;
    if (roomType === 'RECTANGULAR') {
        const wallHeight = rectH * scale;
        return -(wallHeight / 2) + 65;
    }
    const wallHeight = lShapeH * scale + 20;
    return -(wallHeight / 2) + 65;
};

type FootprintRectInFloorGroup = {
    originX: number;
    originY: number;
    width: number;
    depth: number;
};

/**
 * Axis-aligned footprint on the floor line group's local XY plane (world AABB corners
 * projected with Z ignored for extent).
 * Door/drawer state is intentionally ignored by always excluding animated door parts.
 *
 * @param {THREE.Group} cabinetGroup Cabinet root.
 * @param {THREE.Group} floorLineGroup Dashed-line parent group (floor plane, matches cabinet plan pose).
 * @return {{originX: number, originY: number, width: number, depth: number}} Rectangle in floor line-group local XY.
 */
function computeDashedFootprintInLineGroupSpace(
    cabinetGroup: THREE.Group,
    floorLineGroup: THREE.Group
): FootprintRectInFloorGroup {
    cabinetGroup.updateMatrixWorld(true);
    floorLineGroup.updateMatrixWorld(true);

    const worldBounds =
        computeWorldBoundsExcludingAnimatedDoorParts(cabinetGroup);

    if (worldBounds.isEmpty()) {
        return {originX: 0, originY: 0, width: 0, depth: 0};
    }

    const worldToLocal = new THREE.Matrix4()
        .copy(floorLineGroup.matrixWorld)
        .invert();
    const corner = new THREE.Vector3();
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let ix = 0; ix <= 1; ix++) {
        for (let iy = 0; iy <= 1; iy++) {
            for (let iz = 0; iz <= 1; iz++) {
                corner.set(
                    ix ? worldBounds.max.x : worldBounds.min.x,
                    iy ? worldBounds.max.y : worldBounds.min.y,
                    iz ? worldBounds.max.z : worldBounds.min.z
                );
                corner.applyMatrix4(worldToLocal);
                minX = Math.min(minX, corner.x);
                maxX = Math.max(maxX, corner.x);
                minY = Math.min(minY, corner.y);
                maxY = Math.max(maxY, corner.y);
            }
        }
    }

    return {
        originX: minX,
        originY: minY,
        width: maxX - minX,
        depth: maxY - minY,
    };
}

/**
 * Unit vectors in floor line-group local XY toward each wall side, consistent with
 * `addDistanceWallMeasurements` blue arrows for the same cabinet facing.
 *
 * @param {string} cabinetFacing `userData.currentDirection` (e.g. front, back).
 * @return {Object} Keys left, right, front, back mapping to `{x, y}` unit vectors in local XY.
 */
function wallApproachUnitVectorsByFacing(
    cabinetFacing: string
): Record<'left' | 'right' | 'front' | 'back', {x: number; y: number}> {
    if (cabinetFacing === 'back') {
        return {
            left: {x: 1, y: 0},
            right: {x: -1, y: 0},
            front: {x: 0, y: 1},
            back: {x: 0, y: -1},
        };
    }
    return {
        left: {x: -1, y: 0},
        right: {x: 1, y: 0},
        front: {x: 0, y: -1},
        back: {x: 0, y: 1},
    };
}

/**
 * Which footprint edges dashed rays start from so they line up with
 * `createWallDistanceMeasurement` for `currentDirection === 'back'` (left/right and front/back
 * start points are mirrored vs front-facing).
 * Ray lengths are resolved by intersecting those rays with room boundaries.
 *
 * @param {string} cabinetFacing - `userData.currentDirection`.
 * @param {number} minX - Footprint min local X.
 * @param {number} maxX - Footprint max local X.
 * @param {number} minY - Footprint min local Y.
 * @param {number} maxY - Footprint max local Y.
 * @return {object} Ray anchor coordinates `xLeft`, `xRight`, `yFront`, `yBack`.
 */
function dashedWallRayAnchorEdges(
    cabinetFacing: string,
    minX: number,
    maxX: number,
    minY: number,
    maxY: number
): {
    xLeft: number;
    xRight: number;
    yFront: number;
    yBack: number;
} {
    if (cabinetFacing === 'back') {
        return {
            xLeft: maxX,
            xRight: minX,
            yFront: maxY,
            yBack: minY,
        };
    }
    return {
        xLeft: minX,
        xRight: maxX,
        yFront: minY,
        yBack: maxY,
    };
}

type PlanPoint2D = {x: number; y: number};

/**
 * Safe vertex lookup for closed polygons (Array.prototype.at, not bracket indexing).
 *
 * @param {PlanPoint2D[]} polygon Closed outline vertices.
 * @param {number} vertexIndex Vertex index (negative values wrap from the end).
 * @return {PlanPoint2D} Vertex at the wrapped index, or undefined when out of range.
 */
function getClosedPolygonVertex(
    polygon: readonly PlanPoint2D[],
    vertexIndex: number
): PlanPoint2D | undefined {
    return polygon.at(vertexIndex);
}

const HIT_EPSILON = 1e-6;
const DASHED_RAY_DETECTION_OFFSET = 10;

/**
 * Clamps a value to an inset range so detection stays slightly inside the footprint.
 *
 * @param {number} value Coordinate to clamp.
 * @param {number} min Range min.
 * @param {number} max Range max.
 * @param {number} inset Inset applied from both ends.
 * @return {number} Clamped coordinate.
 */
function insetPointWithinRange(
    value: number,
    min: number,
    max: number,
    inset: number
): number {
    const innerMin = Math.min(max, min + inset);
    const innerMax = Math.max(min, max - inset);
    return THREE.MathUtils.clamp(value, innerMin, innerMax);
}

/**
 * Reads the live floor mesh outline and converts it into floor line-group local XY.
 * This keeps dashed clipping aligned with room mesh offsets/trims.
 *
 * @param {THREE.Group} cabinetGroup Cabinet group used to locate scene floor meshes.
 * @param {THREE.Group} floorLineGroup Dashed-line parent group.
 * @return {(PlanPoint2D[] | null)} Floor boundary polygon in local XY, or null if unavailable.
 */
function getRoomBoundaryPolygonFromFloorMesh(
    cabinetGroup: THREE.Group,
    floorLineGroup: THREE.Group
): PlanPoint2D[] | null {
    let root: THREE.Object3D = cabinetGroup;
    while (root.parent) {
        root = root.parent;
    }
    const floorNode =
        root.getObjectByName(ObjectNameMap.floor) ||
        root.getObjectByName(ObjectNameMap.lshapeFloor);
    if (!floorNode || !(floorNode instanceof THREE.Mesh)) {
        return null;
    }
    const geometry = floorNode.geometry as THREE.ExtrudeGeometry;
    const shapeSource = geometry.parameters?.shapes;
    if (!shapeSource) {
        return null;
    }
    const shape = Array.isArray(shapeSource) ? shapeSource[0] : shapeSource;
    const points = shape.extractPoints(24).shape;
    if (!points.length) {
        return null;
    }

    const worldToLocal = new THREE.Matrix4()
        .copy(floorLineGroup.matrixWorld)
        .invert();
    const polygon: PlanPoint2D[] = [];
    for (const point of points) {
        const world = new THREE.Vector3(point.x, point.y, 0);
        floorNode.localToWorld(world);
        world.applyMatrix4(worldToLocal);
        polygon.push({x: world.x, y: world.y});
    }
    if (polygon.length > 1) {
        const first = polygon[0];
        const last = polygon[polygon.length - 1];
        if (
            Math.abs(first.x - last.x) < 1e-6 &&
            Math.abs(first.y - last.y) < 1e-6
        ) {
            polygon.pop();
        }
    }
    return polygon.length >= 3 ? polygon : null;
}

/**
 * Room boundary polygon on the dashed line group's local XY plane.
 * Uses room dimensions directly (scaled) and supports rectangular + L-shaped plans.
 *
 * @param {THREE.Group} floorLineGroup Dashed-line group used as local XY reference.
 * @param {number} floorWorldY World-space floor height used for projection.
 * @param {string} roomType Room key (`RECTANGULAR` or `LSHAPED`).
 * @param {{width: number, length: number}} rectangularDimension Rectangular room dimensions (mm).
 * @param {{rightWidth: number, leftWidth: number, leftDepth: number, rightDepth: number}} lShapedDimension L-shaped dimensions (mm).
 * @param {number} scale mm-to-world scale.
 * @return {PlanPoint2D[]} Closed polygon vertices in floor line-group local XY.
 */
function buildRoomBoundaryPolygonInLineGroupSpace(
    floorLineGroup: THREE.Group,
    floorWorldY: number,
    roomType: string,
    rectangularDimension: {width: number; length: number},
    lShapedDimension: {
        rightWidth: number;
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
    },
    scale: number
): PlanPoint2D[] {
    const worldToLocal = new THREE.Matrix4()
        .copy(floorLineGroup.matrixWorld)
        .invert();
    const toLocalPlan = (worldX: number, worldZ: number): PlanPoint2D => {
        const point = new THREE.Vector3(worldX, floorWorldY, worldZ);
        point.applyMatrix4(worldToLocal);
        return {x: point.x, y: point.y};
    };

    if (roomType === 'RECTANGULAR') {
        const halfW = (rectangularDimension.width * scale) / 2;
        const halfL = (rectangularDimension.length * scale) / 2;
        return [
            toLocalPlan(-halfW, -halfL),
            toLocalPlan(halfW, -halfL),
            toLocalPlan(halfW, halfL),
            toLocalPlan(-halfW, halfL),
        ];
    }

    // L-shape:
    // Outer footprint uses rightWidth x leftWidth.
    // The top-right corner is cut out by (rightWidth - leftDepth) x (leftWidth - rightDepth).
    const fullWidth = lShapedDimension.rightWidth * scale + 50;
    const fullLength = lShapedDimension.leftWidth * scale - 25;
    const innerX = -fullWidth / 2 + lShapedDimension.leftDepth * scale;
    const innerZ = -fullLength / 2 + lShapedDimension.rightDepth * scale;
    const maxX = fullWidth / 2 + 15;
    const maxZ = fullLength / 2;
    const minX = -fullWidth / 2 + 65;
    const minZ = -fullLength / 2 - 25;

    return [
        toLocalPlan(minX, minZ),
        toLocalPlan(maxX, minZ),
        toLocalPlan(maxX, innerZ - 25),
        toLocalPlan(innerX + 65, innerZ - 25),
        toLocalPlan(innerX + 65, maxZ),
        toLocalPlan(minX, maxZ),
    ];
}

/**
 * Edge indices along the inner L-notch (concave corner). Rays ignore these so dashed
 * guides run through the void to the outer wall, but still clip on the outer perimeter.
 *
 * @param {PlanPoint2D[]} polygon Closed floor outline in plan space.
 * @return {Set<number>} Edge indices treated as inner notch (not primary clip targets).
 */
function getLShapeInnerNotchEdgeIndices(polygon: PlanPoint2D[]): Set<number> {
    const vertexCount = polygon.length;
    if (vertexCount < 5) {
        return new Set();
    }

    let signedArea = 0;
    let previousVertex = getClosedPolygonVertex(polygon, vertexCount - 1);
    for (const currentVertex of polygon) {
        if (!previousVertex) {
            break;
        }
        signedArea +=
            previousVertex.x * currentVertex.y -
            previousVertex.y * currentVertex.x;
        previousVertex = currentVertex;
    }
    const isCounterClockwise = signedArea > 0;

    const innerNotchEdgeIndices = new Set<number>();
    for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
        const previous = getClosedPolygonVertex(polygon, vertexIndex - 1);
        const current = getClosedPolygonVertex(polygon, vertexIndex);
        const next = getClosedPolygonVertex(polygon, vertexIndex + 1);
        if (!previous || !current || !next) {
            continue;
        }
        const incomingX = current.x - previous.x;
        const incomingY = current.y - previous.y;
        const outgoingX = next.x - current.x;
        const outgoingY = next.y - current.y;
        const turn = incomingX * outgoingY - incomingY * outgoingX;
        const isReflexVertex = isCounterClockwise
            ? turn < -HIT_EPSILON
            : turn > HIT_EPSILON;
        if (isReflexVertex) {
            innerNotchEdgeIndices.add(
                (vertexIndex - 1 + vertexCount) % vertexCount
            );
            innerNotchEdgeIndices.add(vertexIndex);
        }
    }

    return innerNotchEdgeIndices;
}

type RayPolygonHit = {
    t: number;
    point: PlanPoint2D;
    edgeIndex: number;
};

/**
 * All forward ray–polygon edge crossings, sorted by distance from the origin.
 *
 * @param {PlanPoint2D} origin Ray start.
 * @param {PlanPoint2D} direction Ray direction (need not be normalized).
 * @param {PlanPoint2D[]} polygon Closed floor outline.
 * @return {RayPolygonHit[]} Hits with t > 0, nearest first.
 */
function collectRayPolygonIntersections(
    origin: PlanPoint2D,
    direction: PlanPoint2D,
    polygon: PlanPoint2D[]
): RayPolygonHit[] {
    const rayLenSq = direction.x * direction.x + direction.y * direction.y;
    if (polygon.length < 3 || rayLenSq <= HIT_EPSILON) {
        return [];
    }

    const cross = (a: PlanPoint2D, b: PlanPoint2D) => a.x * b.y - a.y * b.x;
    const hits: RayPolygonHit[] = [];

    const recordHitForEdge = (
        edgeStart: PlanPoint2D,
        edgeEnd: PlanPoint2D,
        edgeIndex: number
    ): void => {
        const edgeVector = {
            x: edgeEnd.x - edgeStart.x,
            y: edgeEnd.y - edgeStart.y,
        };
        const denom = cross(direction, edgeVector);
        if (Math.abs(denom) <= HIT_EPSILON) {
            return;
        }
        const originToEdgeStart = {
            x: edgeStart.x - origin.x,
            y: edgeStart.y - origin.y,
        };
        const t = cross(originToEdgeStart, edgeVector) / denom;
        const u = cross(originToEdgeStart, direction) / denom;
        if (t <= HIT_EPSILON || u < -HIT_EPSILON || u > 1 + HIT_EPSILON) {
            return;
        }
        hits.push({
            t,
            point: {
                x: origin.x + direction.x * t,
                y: origin.y + direction.y * t,
            },
            edgeIndex,
        });
    };

    let firstVertex: PlanPoint2D | null = null;
    let previousVertex: PlanPoint2D | null = null;
    let edgeIndex = 0;
    for (const currentVertex of polygon) {
        if (!firstVertex) {
            firstVertex = currentVertex;
            previousVertex = currentVertex;
            continue;
        }
        if (previousVertex) {
            recordHitForEdge(previousVertex, currentVertex, edgeIndex);
            edgeIndex += 1;
        }
        previousVertex = currentVertex;
    }
    if (firstVertex && previousVertex && firstVertex !== previousVertex) {
        recordHitForEdge(previousVertex, firstVertex, edgeIndex);
    }

    hits.sort((a, b) => a.t - b.t);
    return hits;
}

/**
 * @param {PlanPoint2D} point Point in floor line-group local XY.
 * @param {PlanPoint2D[]} polygon Closed floor outline.
 * @return {boolean} Whether the point lies on walkable floor.
 */
function isPointInsideFloorPolygon(
    point: PlanPoint2D,
    polygon: PlanPoint2D[]
): boolean {
    let inside = false;
    for (let vertexIndex = 0; vertexIndex < polygon.length; vertexIndex++) {
        const current = getClosedPolygonVertex(polygon, vertexIndex);
        const previous = getClosedPolygonVertex(polygon, vertexIndex - 1);
        if (!current || !previous) {
            continue;
        }
        const currentAbove = current.y > point.y;
        const previousAbove = previous.y > point.y;
        if (currentAbove !== previousAbove) {
            const intersectionX =
                ((previous.x - current.x) * (point.y - current.y)) /
                    (previous.y - current.y) +
                current.x;
            if (point.x < intersectionX) {
                inside = !inside;
            }
        }
    }
    return inside;
}

/**
 * Shortens a ray so its endpoint stays inside the floor (no dashes through the L void).
 *
 * @param {PlanPoint2D} origin Ray start.
 * @param {PlanPoint2D} direction Ray direction.
 * @param {number} proposedDistance Candidate travel distance along the ray.
 * @param {PlanPoint2D[]} polygon Floor outline.
 * @return {number} Largest distance along the ray that remains inside the polygon.
 */
function clipRayDistanceToFloorInterior(
    origin: PlanPoint2D,
    direction: PlanPoint2D,
    proposedDistance: number,
    polygon: PlanPoint2D[]
): number {
    if (proposedDistance <= HIT_EPSILON) {
        return 0;
    }

    const endpoint = {
        x: origin.x + direction.x * proposedDistance,
        y: origin.y + direction.y * proposedDistance,
    };
    if (isPointInsideFloorPolygon(endpoint, polygon)) {
        return proposedDistance;
    }

    let insideBound = 0;
    let outsideBound = proposedDistance;
    for (let step = 0; step < 24; step++) {
        const midDistance = (insideBound + outsideBound) / 2;
        const midPoint = {
            x: origin.x + direction.x * midDistance,
            y: origin.y + direction.y * midDistance,
        };
        if (isPointInsideFloorPolygon(midPoint, polygon)) {
            insideBound = midDistance;
        } else {
            outsideBound = midDistance;
        }
    }
    return insideBound;
}

/**
 * Longest forward reach along a ray toward polygon vertices (fallback when no edge hit).
 *
 * @param {PlanPoint2D} origin Ray start.
 * @param {PlanPoint2D} direction Ray direction.
 * @param {PlanPoint2D[]} polygon Floor outline.
 * @return {number} Travel distance along the ray.
 */
function estimateMaxRayDistanceToPolygonVertices(
    origin: PlanPoint2D,
    direction: PlanPoint2D,
    polygon: PlanPoint2D[]
): number {
    let maxDistance = 0;
    for (const vertex of polygon) {
        const vertexDistance = projectPointToRayDistance(
            origin,
            direction,
            vertex
        );
        if (vertexDistance > maxDistance) {
            maxDistance = vertexDistance;
        }
    }
    return maxDistance;
}

/**
 * Resolves how far a dashed guide may run: prefer outer walls on L-shapes, never omit a side,
 * and clip so the segment does not continue through the floor void.
 *
 * @param {PlanPoint2D} origin Ray start.
 * @param {PlanPoint2D} direction Ray direction.
 * @param {PlanPoint2D[]} polygon Floor outline.
 * @param {ReadonlySet<number>} [innerNotchEdgeIndices] L-shaped inner step edges.
 * @return {number} Travel distance along the ray (0 when none).
 */
function resolveDashedRayTravelDistance(
    origin: PlanPoint2D,
    direction: PlanPoint2D,
    polygon: PlanPoint2D[],
    innerNotchEdgeIndices?: ReadonlySet<number>
): number {
    const hits = collectRayPolygonIntersections(origin, direction, polygon);
    let proposedDistance = 0;

    if (hits.length > 0) {
        const outerWallHit = innerNotchEdgeIndices?.size
            ? hits.find(
                  (candidate) => !innerNotchEdgeIndices.has(candidate.edgeIndex)
              )
            : hits[0];
        proposedDistance = outerWallHit ? outerWallHit.t : hits[0].t;
    } else {
        proposedDistance = estimateMaxRayDistanceToPolygonVertices(
            origin,
            direction,
            polygon
        );
    }

    return clipRayDistanceToFloorInterior(
        origin,
        direction,
        proposedDistance,
        polygon
    );
}

/**
 * Projects a point onto a ray direction and returns the forward travel distance.
 *
 * @param {PlanPoint2D} origin Ray start point.
 * @param {PlanPoint2D} direction Ray direction.
 * @param {PlanPoint2D} point Point already lying on the ray.
 * @return {number} Distance parameter along the ray.
 */
function projectPointToRayDistance(
    origin: PlanPoint2D,
    direction: PlanPoint2D,
    point: PlanPoint2D
): number {
    const rayLenSq = direction.x * direction.x + direction.y * direction.y;
    if (rayLenSq <= HIT_EPSILON) {
        return 0;
    }
    return (
        ((point.x - origin.x) * direction.x +
            (point.y - origin.y) * direction.y) /
        rayLenSq
    );
}

/**
 * Floor-level dashed guides: footprint rectangle plus eight rays to walls.
 * Rays clip to the room outline in plan view. On L-shaped rooms, dashes target outer
 * walls (skipping the inner notch step), fall back so every side still draws, and
 * endpoints are clipped so segments do not continue through the floor void.
 *
 * @param {THREE.Group} cabinetGroup Cabinet to measure from.
 * @param {function} handleLinesAndGroup Registers line objects with the room arrow handler.
 * @param {string} roomType `RECTANGULAR` or L-shaped key.
 * @param {object} rectangularDimension Room width/length/height (mm).
 * @param {object} lShapedDimension L-leg dimensions (mm).
 * @param {number} scale mm to world units.
 * @return {(THREE.Group|null)} Group parented beside the cabinet, or null if the cabinet has no parent.
 */
export const addDashedLines = (
    cabinetGroup: THREE.Group,
    handleLinesAndGroup: (
        lines: THREE.Line[],
        lineGroup?: THREE.Group<THREE.Object3DEventMap>
    ) => void,
    roomType: string,
    rectangularDimension: {width: number; length: number; height?: number},
    lShapedDimension: {
        rightWidth: number;
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
        height?: number;
    },
    scale: number
): THREE.Group | null => {
    const dashedStyle = {
        color: 0x000000,
        dashSize: 5,
        gapSize: 5,
        renderOrder: 5,
    } as const;

    const floorWorldY = getFloorY(
        roomType,
        scale,
        rectangularDimension.height ?? 0,
        lShapedDimension.height ?? 0
    );

    const floorLineGroup = new THREE.Group();
    floorLineGroup.name = 'distanceWallMeasurementsDashed';
    floorLineGroup.renderOrder = dashedStyle.renderOrder;
    floorLineGroup.userData.id =
        (cabinetGroup.userData.id as string) ||
        'distanceWallMeasurementsDashed';
    floorLineGroup.userData.cabinetTargetUuid = cabinetGroup.uuid;
    floorLineGroup.userData.floorWorldY = floorWorldY;

    const dashedSegments: THREE.Line[] = [];
    /** One material per rebuild; all segments share it (avoids clone-per-line). */
    let sharedDashedMaterial: THREE.LineDashedMaterial | undefined;

    const disposeDashedSegmentsAndMaterial = () => {
        for (const seg of dashedSegments) {
            floorLineGroup.remove(seg);
            seg.geometry?.dispose();
        }
        dashedSegments.length = 0;
        sharedDashedMaterial?.dispose();
        sharedDashedMaterial = undefined;
    };

    const syncFloorGroupTransformToCabinet = () => {
        floorLineGroup.userData.floorWorldY = floorWorldY;
        const posX = cabinetGroup.position.x;
        const posZ = cabinetGroup.position.z;
        // Skip sync when cabinet position is invalid (prevents NaN line geometry)
        if (!isFinite(posX) || !isFinite(posZ)) {
            return;
        }
        syncOverlayTransformToCabinet(floorLineGroup, cabinetGroup);
        // Dashed guides always sit on the floor plane
        floorLineGroup.position.y = floorWorldY;
        floorLineGroup.updateMatrixWorld(true);
        cabinetGroup.updateMatrixWorld(true);
    };

    const rebuildDashedWallGuides = () => {
        disposeDashedSegmentsAndMaterial();

        const facing =
            (cabinetGroup.userData.currentDirection as string) || 'front';
        const wallUnit = wallApproachUnitVectorsByFacing(facing);

        const footprint = computeDashedFootprintInLineGroupSpace(
            cabinetGroup,
            floorLineGroup
        );

        const floorZ = 0;
        const minX = footprint.originX;
        const maxX = footprint.originX + footprint.width;
        const minY = footprint.originY;
        const maxY = footprint.originY + footprint.depth;

        if (
            footprint.width <= 0 ||
            footprint.depth <= 0 ||
            !isFinite(minX) ||
            !isFinite(maxX) ||
            !isFinite(minY) ||
            !isFinite(maxY)
        ) {
            return;
        }

        const roomBoundaryPolygon =
            getRoomBoundaryPolygonFromFloorMesh(cabinetGroup, floorLineGroup) ??
            buildRoomBoundaryPolygonInLineGroupSpace(
                floorLineGroup,
                floorWorldY,
                roomType,
                {
                    width: rectangularDimension.width,
                    length: rectangularDimension.length,
                },
                {
                    rightWidth: lShapedDimension.rightWidth,
                    leftWidth: lShapedDimension.leftWidth,
                    leftDepth: lShapedDimension.leftDepth,
                    rightDepth: lShapedDimension.rightDepth,
                },
                scale
            );
        if (roomBoundaryPolygon.length < 3) {
            return;
        }

        const lShapeInnerNotchEdgeIndices =
            roomType === 'LSHAPED'
                ? getLShapeInnerNotchEdgeIndices(roomBoundaryPolygon)
                : undefined;

        sharedDashedMaterial = new THREE.LineDashedMaterial({
            color: dashedStyle.color,
            dashSize: dashedStyle.dashSize,
            gapSize: dashedStyle.gapSize,
        });

        const material = sharedDashedMaterial;

        const addDashedSegment = (
            start: THREE.Vector3,
            end: THREE.Vector3,
            segmentName: string
        ) => {
            if (
                !isFinite(start.x) ||
                !isFinite(start.y) ||
                !isFinite(start.z) ||
                !isFinite(end.x) ||
                !isFinite(end.y) ||
                !isFinite(end.z)
            ) {
                return;
            }
            const geometry = new THREE.BufferGeometry().setFromPoints([
                start,
                end,
            ]);
            const line = new THREE.Line(geometry, material);
            line.computeLineDistances();
            line.renderOrder = dashedStyle.renderOrder;
            line.name = segmentName;
            floorLineGroup.add(line);
            dashedSegments.push(line);
        };

        const addDashedRayToRoomBoundary = (
            startX: number,
            startY: number,
            wall: 'left' | 'right' | 'front' | 'back',
            segmentName: string
        ) => {
            let directionX = 0;
            let directionY = 0;
            switch (wall) {
                case 'left':
                    directionX = wallUnit.left.x;
                    directionY = wallUnit.left.y;
                    break;
                case 'right':
                    directionX = wallUnit.right.x;
                    directionY = wallUnit.right.y;
                    break;
                case 'front':
                    directionX = wallUnit.front.x;
                    directionY = wallUnit.front.y;
                    break;
                case 'back':
                    directionX = wallUnit.back.x;
                    directionY = wallUnit.back.y;
                    break;
                default:
                    return;
            }
            const rayOrigin = {x: startX, y: startY};
            const detectionOrigin =
                wall === 'left' || wall === 'right'
                    ? {
                          x: startX,
                          y: insetPointWithinRange(
                              startY,
                              minY,
                              maxY,
                              DASHED_RAY_DETECTION_OFFSET
                          ),
                      }
                    : {
                          x: insetPointWithinRange(
                              startX,
                              minX,
                              maxX,
                              DASHED_RAY_DETECTION_OFFSET
                          ),
                          y: startY,
                      };
            const rayDirection = {x: directionX, y: directionY};
            let travelDistance = resolveDashedRayTravelDistance(
                rayOrigin,
                rayDirection,
                roomBoundaryPolygon,
                lShapeInnerNotchEdgeIndices
            );
            if (
                travelDistance <= HIT_EPSILON &&
                (Math.abs(detectionOrigin.x - rayOrigin.x) > HIT_EPSILON ||
                    Math.abs(detectionOrigin.y - rayOrigin.y) > HIT_EPSILON)
            ) {
                const detectionSteps = Math.max(
                    2,
                    Math.ceil(DASHED_RAY_DETECTION_OFFSET / 10)
                );
                for (let step = 1; step <= detectionSteps; step++) {
                    const progress = step / detectionSteps;
                    const candidateOrigin = {
                        x: THREE.MathUtils.lerp(
                            rayOrigin.x,
                            detectionOrigin.x,
                            progress
                        ),
                        y: THREE.MathUtils.lerp(
                            rayOrigin.y,
                            detectionOrigin.y,
                            progress
                        ),
                    };
                    const candidateDistance = resolveDashedRayTravelDistance(
                        candidateOrigin,
                        rayDirection,
                        roomBoundaryPolygon,
                        lShapeInnerNotchEdgeIndices
                    );
                    if (candidateDistance > HIT_EPSILON) {
                        travelDistance = candidateDistance;
                        break;
                    }
                }
            }
            if (travelDistance <= HIT_EPSILON) {
                return;
            }
            addDashedSegment(
                new THREE.Vector3(startX, startY, floorZ),
                new THREE.Vector3(
                    startX + directionX * travelDistance,
                    startY + directionY * travelDistance,
                    floorZ
                ),
                segmentName
            );
        };

        const {xLeft, xRight, yFront, yBack} = dashedWallRayAnchorEdges(
            facing,
            minX,
            maxX,
            minY,
            maxY
        );
        const cabinetValues = cabinetGroup.userData
            .values as RoomPlannerProduct;
        const upperCabinetDashedOffset = cabinetGroup.userData.isUpperProduct
            ? -7
            : 0;
        const frontEdgeOffset = upperCabinetDashedOffset;
        const backEdgeOffset = upperCabinetDashedOffset;
        // Placeholders have empty values; carcase offset is cabinet-only
        const carcThickness = cabinetGroup.userData?.isPlaceholder
            ? 0
            : (cabinetValues?.cabinet_carc_thickness ?? 0) * 0.2;
        // Rays from footprint edges to the nearest room boundary in each wall direction.
        addDashedRayToRoomBoundary(
            xLeft,
            minY - frontEdgeOffset,
            'left',
            'dash_left_minY'
        );
        addDashedRayToRoomBoundary(
            xLeft,
            maxY + backEdgeOffset,
            'left',
            'dash_left_maxY'
        );
        addDashedRayToRoomBoundary(
            xRight,
            minY - frontEdgeOffset,
            'right',
            'dash_right_minY'
        );
        addDashedRayToRoomBoundary(
            xRight,
            maxY + backEdgeOffset,
            'right',
            'dash_right_maxY'
        );
        addDashedRayToRoomBoundary(minX, yFront, 'front', 'dash_front_minX');
        addDashedRayToRoomBoundary(maxX, yFront, 'front', 'dash_front_maxX');
        addDashedRayToRoomBoundary(minX, yBack, 'back', 'dash_back_minX');
        addDashedRayToRoomBoundary(maxX, yBack, 'back', 'dash_back_maxX');

        // Closed rectangle on the cabinet footprint (local XY at floor height).
        addDashedSegment(
            new THREE.Vector3(minX, minY - frontEdgeOffset, floorZ),
            new THREE.Vector3(
                maxX + carcThickness,
                minY - frontEdgeOffset,
                floorZ
            ),
            'footprint_front'
        );
        addDashedSegment(
            new THREE.Vector3(maxX, minY - frontEdgeOffset, floorZ),
            new THREE.Vector3(maxX, maxY + backEdgeOffset, floorZ),
            'footprint_right'
        );
        addDashedSegment(
            new THREE.Vector3(
                maxX + carcThickness,
                maxY + backEdgeOffset,
                floorZ
            ),
            new THREE.Vector3(minX, maxY + backEdgeOffset, floorZ),
            'footprint_back'
        );
        addDashedSegment(
            new THREE.Vector3(minX, maxY + backEdgeOffset, floorZ),
            new THREE.Vector3(minX, minY - frontEdgeOffset, floorZ),
            'footprint_left'
        );
    };

    floorLineGroup.userData.updateVisibility = (isActive: boolean) => {
        floorLineGroup.visible = isActive;
        setVisibility(dashedSegments, isActive);
    };

    floorLineGroup.visible = false;

    floorLineGroup.userData.onDrag = () => {
        syncFloorGroupTransformToCabinet();
        rebuildDashedWallGuides();
        handleLinesAndGroup(dashedSegments, floorLineGroup);
    };

    floorLineGroup.userData.onDragEnd = () => {
        const isMovementMode = cabinetGroup.userData.isMovementMode as boolean;
        const isActiveGroup = cabinetGroup.userData.isActiveGroup as boolean;
        if (isActiveGroup && isMovementMode) {
            floorLineGroup.visible = true;
            setVisibility(dashedSegments, true);
        } else {
            floorLineGroup.visible = false;
            setVisibility(dashedSegments, false);
        }
    };

    cabinetGroup.userData.distanceWallDashedLineGroup = floorLineGroup;
    cabinetGroup.userData.showDistanceWallDashedLines = (show: boolean) => {
        setVisibility(dashedSegments, show);
    };

    floorLineGroup.userData.dispose = () => {
        disposeDashedSegmentsAndMaterial();
        if (floorLineGroup.parent) {
            floorLineGroup.parent.remove(floorLineGroup);
        }
        floorLineGroup.userData = {
            ...floorLineGroup.userData,
            isDisposed: true,
        };
    };

    cabinetGroup.userData.disposeDistanceWallDashedLines = () => {
        (floorLineGroup.userData as {dispose: () => void}).dispose();
    };

    if (!cabinetGroup.parent) {
        return null;
    }
    cabinetGroup.parent.add(floorLineGroup);
    syncFloorGroupTransformToCabinet();
    rebuildDashedWallGuides();
    handleLinesAndGroup(dashedSegments, floorLineGroup);
    floorLineGroup.visible = false;

    cabinetGroup.userData.setDistanceWallDashedLinesActive = (
        isActive: boolean
    ) => {
        (
            floorLineGroup.userData as {
                updateVisibility: (visible: boolean) => void;
            }
        ).updateVisibility(isActive);
    };

    return floorLineGroup;
};
